import React, { useState, useRef, useCallback, useEffect } from "react";
import {
  Box,
  Typography,
  IconButton,
  Tooltip,
  Button,
  Chip,
  Select,
  MenuItem,
  CircularProgress,
  Menu,
  ListItemIcon,
  ListItemText,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Switch,
  FormControlLabel,
} from "@mui/material";
import {
  SmartDisplay as SmartDisplayIcon,
  ArrowUpward as ArrowUpwardIcon,
  AttachFile as AttachFileIcon,
  DeleteOutline as DeleteIcon,
  MoreVert as MoreVertIcon,
  Download as DownloadIcon,
  AddPhotoAlternate as UseAsRefIcon,
  SaveAlt as SaveAltIcon,
  AutoFixHigh as WatermarkIcon,
  Add as AddIcon,
  ExpandMore as ExpandMoreIcon,
  ViewTimeline as StoryboardIcon,
  Extension as ExtendIcon,
  Hd as HdIcon,
  FourK as FourKIcon,
  Settings as SettingsIcon,
  LastPage as LastFrameIcon,
  OpenInNew as OpenInNewIcon,
  InfoOutlined as InfoIcon,
  Image as ImageIcon,
  AutoAwesome as AutoAwesomeIcon,
} from "@mui/icons-material";
import { useUserContext } from "../../context/UserContext";
import { useNotification } from "../../context/NotificationContext";
import { THEME_COLORS } from "../../context/constants";
import {
  createSora2TextToVideo,
  createSora2ImageToVideo,
  createSora2ProTextToVideo,
  createSora2ProImageToVideo,
  createSora2Storyboard,
  createSeedanceTextToVideo,
  createSeedanceImageToVideo,
  createSeedance15Pro,
  createSeedance2,
  createSeedance2Fast,
  createSeedance25,
  createVeo3Generate,
  createVeo3Extend,
  getVeo31080p,
  startVeo3FourK,
  getVeo3Task,
  getVeo3Tasks,
  getSora2Task,
  getSora2Tasks,
  getSeedanceTask,
  getSeedanceTasks,
  uploadBackgroundAssetV2,
  removeSora2MediaFromWorkspace,
  removeSeedanceMediaFromWorkspace,
  removeVeo3MediaFromWorkspace,
  saveSora2ToAssets,
  saveSeedanceToAssets,
  saveVeo3ToAssets,
  removeSora2Watermark,
  extractLastFrame,
  createNanoBanana,
  getNanoBananaTask,
  getNanoBananaTasks,
  saveNanoBananaToAssets,
  removeNanoBananaMediaFromWorkspace,
  createGeminiGenerate,
  getGeminiTask,
  getGeminiTasks,
  saveGeminiToAssets,
  removeGeminiMediaFromWorkspace,
  createWan3Generate,
  createWan3PrimeGenerate,
  getWan3Task,
  getWan3Tasks,
  saveWan3ToAssets,
  removeWan3MediaFromWorkspace,
  createHetznerGenerate,
  getHetznerTask,
  getHetznerTasks,
  saveHetznerToAssets,
  removeHetznerMediaFromWorkspace,
  createPromptSuggestion,
  getPromptSuggestionResult,
} from "../../client/api";
import { isFreeUser } from "../../utils/utils";
import {
  Sora2Media,
  Sora2AspectRatio,
  Sora2Frames,
  Sora2Shot,
  Sora2Size,
  SeedanceMedia,
  SeedanceAspectRatio,
  SeedanceResolution,
  SeedanceDuration,
  Veo3Media,
  Veo3Model,
  Veo3ExtendModel,
  Veo3AspectRatio,
  NanoBananaMedia,
  NanoBananaAspectRatio,
  NanoBananaResolution,
  NanoBananaOutputFormat,
  GeminiMedia,
  GeminiAspectRatio,
  GeminiResolution,
  GeminiDuration,
  SeedanceOutputFormat,
  Wan3Media,
  Wan3Resolution,
  Wan3AspectRatio,
  HetznerMedia,
} from "../../context/types";
import useStyles from "./styles";

const MAX_CHARS = 10000;
const SEEDANCE_15PRO_MAX_CHARS = 2500;
const SEEDANCE_2_MAX_CHARS = 20000;
const NANO_BANANA_MAX_CHARS = 20000;
const POLL_INTERVAL = 5000;
const MAX_POLL_ATTEMPTS = 1200; // 10 minutes at 5s intervals

type ModelMode = "sora2" | "sora2pro" | "storyboard" | "seedance" | "seedance15pro" | "seedance2" | "seedance2fast" | "seedance25" | "veo3" | "nanobanana" | "gemini" | "wan3" | "wan3prime" | "hetzner";

// Approximate kie.ai credit cost per engine, shown as a static disclaimer under the prompt
// input. Derived from kie.ai's pricing tables (1 credit = $0.005) — treat as ground truth,
// not re-derived here. Veo3 is computed dynamically from `veo3Model` (see getCostDisclaimer).
const ENGINE_COST_DISCLAIMERS: Record<ModelMode, string> = {
  sora2: "Est. cost: ~$0.15 for a 10s video, ~$0.175 for 15s.",
  sora2pro: "Est. cost — Standard quality: ~$0.75 (10s) / ~$1.35 (15s). High quality: ~$1.65 (10s) / ~$3.15 (15s).",
  storyboard: "Est. cost: ~$0.75 for a 10s storyboard, ~$1.35 for 15–25s.",
  seedance: "Est. cost: ~$0.014/s at 480p, ~$0.03/s at 720p, ~$0.07/s at 1080p.",
  seedance15pro: "Est. cost — no audio: ~$0.009/s (480p) · ~$0.018/s (720p) · ~$0.038/s (1080p). With audio: ~$0.018/s · ~$0.035/s · ~$0.075/s.",
  seedance2: "Est. cost — with a reference video: ~$0.058/s (480p) · ~$0.125/s (720p) · ~$0.31/s (1080p). Without: ~$0.095/s · ~$0.205/s · ~$0.51/s. Billed as (input + output) duration × rate.",
  seedance2fast: "Est. cost — with a reference video: ~$0.045/s (480p) · ~$0.10/s (720p). Without: ~$0.078/s · ~$0.165/s. Billed as (input + output) duration × rate.",
  seedance25: "Est. cost — with a reference video: ~$0.085/s (480p) · ~$0.19/s (720p) · ~$0.34/s (1080p). Without: ~$0.14/s · ~$0.32/s · ~$0.57/s. Billed as (input + output) duration × rate.",
  veo3: "", // handled dynamically in getCostDisclaimer, based on veo3Model
  nanobanana: "Est. cost: ~$0.04 per image (1K), ~$0.06 (2K), ~$0.09 (4K).",
  gemini: "Est. cost — without a reference video: ~$0.315–$0.63 depending on length (4–10s) at 360p/720p/1080p, ~$0.735–$1.05 at 4K. With a reference video: flat ~$0.84 (up to 1080p) or ~$1.26 (4K), regardless of length.",
  wan3: "Est. cost: ~$0.04/s (480p) · ~$0.08/s (720p) · ~$0.16/s (1080p). Billed as (input + output) duration × rate.",
  wan3prime: "Est. cost: ~$0.061/s (480p) · ~$0.126/s (720p) · ~$0.252/s (1080p). Billed as (input + output) duration × rate.",
  hetzner: "Free of charge — runs on our own GPU server (Hetzner), not billed per generation. Under active testing; timing/stability may vary.",
};

// Veo3 cost depends on the selected tier (Quality vs Fast), not a static per-engine string.
const getCostDisclaimer = (modelMode: ModelMode, veo3Model: string): string => {
  if (modelMode === "veo3") {
    return veo3Model === "veo3_fast"
      ? "Est. cost: ~$0.30 per video (Fast mode)."
      : "Est. cost: ~$1.25 per video (Quality mode).";
  }
  return ENGINE_COST_DISCLAIMERS[modelMode];
};

// Union type for any media item in the carousel/modal
type AnyMedia = Sora2Media | SeedanceMedia | Veo3Media | NanoBananaMedia | GeminiMedia | Wan3Media | HetznerMedia;

// Type guards key off each engine's unique, reliable `model` field rather than
// presence-based heuristics: Seedance 2.5 media also carries `outputFormat` and
// Gemini media also carries `resolution`, so the old presence checks would
// misclassify them. Nano Banana always sets model to the literal 'nano-banana-2'.
const isNanoBananaMedia = (media: AnyMedia): media is NanoBananaMedia =>
  (media as any).model === "nano-banana-2";

const isSeedanceMedia = (media: AnyMedia): media is SeedanceMedia =>
  "model" in media && typeof (media as any).model === "string" && (media as any).model.startsWith("bytedance/");

const isGeminiMedia = (media: AnyMedia): media is GeminiMedia =>
  "model" in media && typeof (media as any).model === "string" && (media as any).model.startsWith("google/");

const isWan3Media = (media: AnyMedia): media is Wan3Media =>
  typeof media.model === "string" && media.model.startsWith("wan/");

const isHetznerMedia = (media: AnyMedia): media is HetznerMedia =>
  (media as any).model === "self-hosted/minimax-h3";

const DEFAULT_SHOT: Sora2Shot = { Scene: "", duration: 5 };

// fourKState is unique to Veo3 — nothing else has it.
const isVeo3Media = (media: AnyMedia): media is Veo3Media => "fourKState" in media;

// Image-producing engines (currently only Nano Banana). Used to render <img> vs <video>.
const isImageMedia = (media: AnyMedia): boolean => isNanoBananaMedia(media);

// Seedance, Gemini, and Wan 3.0 use the same "in progress" state names
const SEEDANCE_PENDING_STATES: SeedanceMedia["state"][] = ["waiting", "queuing", "generating"];
const GEMINI_PENDING_STATES: GeminiMedia["state"][] = ["waiting", "queuing", "generating"];
const WAN3_PENDING_STATES: Wan3Media["state"][] = ["waiting", "queuing", "generating"];
const VEO3_PENDING_STATES: Veo3Media["state"][] = ["pending", "processing"];
const HETZNER_PENDING_STATES: HetznerMedia["state"][] = ["waiting", "generating"];

// Smart video URL: prefer watermark-removed URL when available (Sora2 only)
const getVideoUrl = (media: AnyMedia): string | undefined => {
  if (isNanoBananaMedia(media)) {
    return media.resultUrls?.[0];
  }
  if (isVeo3Media(media)) {
    if (media.videoUrl4k && media.fourKState === "success") return media.videoUrl4k;
    if (media.videoUrl1080p) return media.videoUrl1080p;
    if (media.videoUrl) return media.videoUrl;
    return media.resultUrls?.[0];
  }
  if (!isSeedanceMedia(media) && !isNanoBananaMedia(media) && !isGeminiMedia(media) && !isWan3Media(media) && !isHetznerMedia(media)) {
    if (media.watermarkRemoved && media.watermarkRemovedResultUrls?.length) {
      return media.watermarkRemovedResultUrls[0];
    }
  }
  return media.resultUrls?.[0];
};

// Normalize state across engines for UI rendering
const isMediaSuccess = (media: AnyMedia) => media.state === "success";
const isMediaFail = (media: AnyMedia) => media.state === "fail";

const PromptToVideo: React.FC = () => {
  const classes = useStyles();
  const { currentWorkspace, user } = useUserContext();
  const { addNotification } = useNotification();
  const fileInputRef = useRef<HTMLInputElement>(null);

  // State
  const [prompt, setPrompt] = useState("");
  const [quantity, setQuantity] = useState(1);
  const [isGenerating, setIsGenerating] = useState(false);
  const [currentGenMedia, setCurrentGenMedia] = useState<AnyMedia | null>(null);
  const [workspaceMedia, setWorkspaceMedia] = useState<Sora2Media[]>([]);
  const [seedanceMedia, setSeedanceMedia] = useState<SeedanceMedia[]>([]);
  const [isLoadingMedia, setIsLoadingMedia] = useState(false);
  const [isLoadingSeedanceMedia, setIsLoadingSeedanceMedia] = useState(false);
  const [aspectRatio, setAspectRatio] = useState<Sora2AspectRatio>("portrait");
  const [nFrames, setNFrames] = useState<Sora2Frames>("10");
  const [uploadedImages, setUploadedImages] = useState<{ previewUrl: string; publicUrl: string }[]>(
    []
  );
  const [isUploadingImage, setIsUploadingImage] = useState(false);
  const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null);
  const [menuMedia, setMenuMedia] = useState<AnyMedia | null>(null);
  const [selectedMedia, setSelectedMedia] = useState<AnyMedia | null>(null);
  const [pendingDeleteMedia, setPendingDeleteMedia] = useState<AnyMedia | null>(null);
  const [modelMode, setModelMode] = useState<ModelMode>("seedance15pro");
  const [modelMenuAnchor, setModelMenuAnchor] = useState<HTMLElement | null>(null);
  const [shots, setShots] = useState<Sora2Shot[]>([{ ...DEFAULT_SHOT }, { ...DEFAULT_SHOT }]);
  const [size, setSize] = useState<Sora2Size>("high");
  const [isSavingToAssets, setIsSavingToAssets] = useState(false);
  // Seedance settings
  const [seedanceAspectRatio, setSeedanceAspectRatio] = useState<SeedanceAspectRatio>("9:16");
  const [seedanceResolution, setSeedanceResolution] = useState<SeedanceResolution>("720p");
  const [seedanceDuration, setSeedanceDuration] = useState<SeedanceDuration>("5");
  // Seedance 1.5 Pro settings
  const [seedance15Duration, setSeedance15Duration] = useState<"4" | "8" | "12">("8");
  const [fixedLens, setFixedLens] = useState(false);
  const [generateAudio, setGenerateAudio] = useState(false);
  // Seedance 2.0 settings
  // aspect ratio includes 'adaptive' for 2.0 — reuse seedanceAspectRatio state but
  // keep a dedicated state so switching engines doesn't leak the 'adaptive' value.
  const [seedance2AspectRatio, setSeedance2AspectRatio] = useState<
    "1:1" | "4:3" | "3:4" | "16:9" | "9:16" | "21:9" | "adaptive"
  >("9:16");
  const [seedance2Resolution, setSeedance2Resolution] = useState<"480p" | "720p">("720p");
  const [seedance2Duration, setSeedance2Duration] = useState<number>(5);
  const [seedance2GenerateAudio, setSeedance2GenerateAudio] = useState(true);
  const [seedance2WebSearch, setSeedance2WebSearch] = useState(false);
  const [seedance2NsfwChecker, setSeedance2NsfwChecker] = useState(false);
  const [seedance2AdvancedAnchor, setSeedance2AdvancedAnchor] = useState<null | HTMLElement>(null);
  // Seedance 2.0 Fast settings
  // aspect ratio excludes 'adaptive' for fast mode; default duration is 15 (matches backend)
  const [seedance2FastAspectRatio, setSeedance2FastAspectRatio] = useState<
    "1:1" | "4:3" | "3:4" | "16:9" | "9:16" | "21:9"
  >("9:16");
  const [seedance2FastResolution, setSeedance2FastResolution] = useState<"480p" | "720p">("720p");
  const [seedance2FastDuration, setSeedance2FastDuration] = useState<number>(5);
  const [seedance2FastGenerateAudio, setSeedance2FastGenerateAudio] = useState(true);
  const [seedance2FastWebSearch, setSeedance2FastWebSearch] = useState(false);
  const [seedance2FastNsfwChecker, setSeedance2FastNsfwChecker] = useState(true);
  const [seedance2FastAdvancedAnchor, setSeedance2FastAdvancedAnchor] = useState<null | HTMLElement>(null);
  // Seedance 2.5 settings — mirrors 2.0 closely, plus outputFormat/returnLastFrame and a
  // higher duration cap (native up to 30s vs 2.0's 15s)
  const [seedance25AspectRatio, setSeedance25AspectRatio] = useState<
    "1:1" | "4:3" | "3:4" | "16:9" | "9:16" | "21:9" | "adaptive"
  >("9:16");
  const [seedance25Resolution, setSeedance25Resolution] = useState<"480p" | "720p" | "1080p">("720p");
  const [seedance25Duration, setSeedance25Duration] = useState<number>(5);
  const [seedance25GenerateAudio, setSeedance25GenerateAudio] = useState(true);
  const [seedance25ReturnLastFrame, setSeedance25ReturnLastFrame] = useState(false);
  const [seedance25OutputFormat, setSeedance25OutputFormat] = useState<SeedanceOutputFormat>("mp4");
  const [seedance25WebSearch, setSeedance25WebSearch] = useState(false);
  const [seedance25NsfwChecker, setSeedance25NsfwChecker] = useState(false);
  const [seedance25AdvancedAnchor, setSeedance25AdvancedAnchor] = useState<null | HTMLElement>(null);
  // Veo3 media & settings
  const [veo3Media, setVeo3Media] = useState<Veo3Media[]>([]);
  const [isLoadingVeo3Media, setIsLoadingVeo3Media] = useState(false);
  const [veo3Model, setVeo3Model] = useState<Veo3Model>("veo3_fast");
  const [veo3AspectRatio, setVeo3AspectRatio] = useState<Veo3AspectRatio>("9:16");
  const [veo3ImageGenType, setVeo3ImageGenType] = useState<
    "FIRST_AND_LAST_FRAMES_2_VIDEO" | "REFERENCE_2_VIDEO"
  >("FIRST_AND_LAST_FRAMES_2_VIDEO");
  const [showVeo3Advanced, setShowVeo3Advanced] = useState(false);
  const [veo3Seeds, setVeo3Seeds] = useState<number | undefined>(undefined);
  const [veo3EnableTranslation, setVeo3EnableTranslation] = useState(false);
  const [veo3Watermark, setVeo3Watermark] = useState("");
  // Veo3 extend dialog
  const [extendDialogOpen, setExtendDialogOpen] = useState(false);
  const [extendSourceMedia, setExtendSourceMedia] = useState<Veo3Media | null>(null);
  const [extendPrompt, setExtendPrompt] = useState("");
  const [extendModel, setExtendModel] = useState<Veo3ExtendModel>("fast");
  const [isExtending, setIsExtending] = useState(false);
  // Prompt suggestion dialog (claude-worker, trained on winning ads)
  const [promptSuggestionDialogOpen, setPromptSuggestionDialogOpen] = useState(false);
  const [promptSuggestionInstructions, setPromptSuggestionInstructions] = useState("");
  const [isGeneratingSuggestion, setIsGeneratingSuggestion] = useState(false);
  // Veo3 action states
  const [isFetching1080p, setIsFetching1080p] = useState(false);
  const [isStarting4K, setIsStarting4K] = useState(false);
  // Nano Banana media & settings
  const [nanoBananaMedia, setNanoBananaMedia] = useState<NanoBananaMedia[]>([]);
  const [isLoadingNanoBananaMedia, setIsLoadingNanoBananaMedia] = useState(false);
  const [nanoBananaAspectRatio, setNanoBananaAspectRatio] =
    useState<NanoBananaAspectRatio>("auto");
  const [nanoBananaResolution, setNanoBananaResolution] = useState<NanoBananaResolution>("1K");
  const [nanoBananaOutputFormat, setNanoBananaOutputFormat] =
    useState<NanoBananaOutputFormat>("jpg");
  // Gemini media & settings
  const [geminiMedia, setGeminiMedia] = useState<GeminiMedia[]>([]);
  const [isLoadingGeminiMedia, setIsLoadingGeminiMedia] = useState(false);
  const [geminiAspectRatio, setGeminiAspectRatio] = useState<GeminiAspectRatio>("16:9");
  const [geminiResolution, setGeminiResolution] = useState<GeminiResolution>("720p");
  const [geminiDuration, setGeminiDuration] = useState<GeminiDuration>("8");
  const [geminiSeed, setGeminiSeed] = useState<number | undefined>(undefined);
  // Wan 3.0 Video media & settings
  const [wan3Media, setWan3Media] = useState<Wan3Media[]>([]);
  const [isLoadingWan3Media, setIsLoadingWan3Media] = useState(false);
  const [hetznerMedia, setHetznerMedia] = useState<HetznerMedia[]>([]);
  const [isLoadingHetznerMedia, setIsLoadingHetznerMedia] = useState(false);
  // Local LLM Engine settings
  const [hetznerResolution, setHetznerResolution] = useState<string>("1344x768");
  const [hetznerDurationS, setHetznerDurationS] = useState<number>(3);
  const [wan3Resolution, setWan3Resolution] = useState<Wan3Resolution>("1080P");
  const [wan3AspectRatio, setWan3AspectRatio] = useState<Wan3AspectRatio>("adaptive");
  const [wan3Duration, setWan3Duration] = useState<number>(5);
  const [wan3Audio, setWan3Audio] = useState(true);
  const [wan3Seed, setWan3Seed] = useState<number | undefined>(undefined);
  const [wan3NsfwChecker, setWan3NsfwChecker] = useState(true);
  const [wan3AdvancedAnchor, setWan3AdvancedAnchor] = useState<null | HTMLElement>(null);

  // Fetch Sora2 workspace media on mount
  useEffect(() => {
    if (!currentWorkspace) return;
    const fetchMedia = async () => {
      setIsLoadingMedia(true);
      try {
        const response = await getSora2Tasks({
          workspaceId: currentWorkspace._id,
        });
        if (response.success) {
          setWorkspaceMedia(response.data);
        }
      } catch (error) {
        console.error("Error fetching workspace media:", error);
      } finally {
        setIsLoadingMedia(false);
      }
    };
    fetchMedia();
  }, [currentWorkspace]);

  // Fetch Seedance workspace media on mount
  useEffect(() => {
    if (!currentWorkspace) return;
    const fetchSeedanceMedia = async () => {
      setIsLoadingSeedanceMedia(true);
      try {
        const response = await getSeedanceTasks({
          workspaceId: currentWorkspace._id,
        });
        if (response.success) {
          setSeedanceMedia(response.data);
        }
      } catch (error) {
        console.error("Error fetching seedance media:", error);
      } finally {
        setIsLoadingSeedanceMedia(false);
      }
    };
    fetchSeedanceMedia();
  }, [currentWorkspace]);

  // Poll processing/pending Sora2 items
  useEffect(() => {
    const pendingItems = workspaceMedia.filter(
      (m) => m.state === "pending" || m.state === "processing"
    );
    if (pendingItems.length === 0) return;

    const intervalId = setInterval(async () => {
      for (const item of pendingItems) {
        try {
          const response = await getSora2Task(item.taskId);
          const updated = response.data;
          if (updated.state !== item.state) {
            setWorkspaceMedia((prev) => prev.map((m) => (m._id === item._id ? updated : m)));
            setSelectedMedia((prev) => (prev?._id === item._id ? updated : prev));
          }
        } catch (error) {
          console.error("Error polling pending item:", error);
        }
      }
    }, POLL_INTERVAL);

    return () => clearInterval(intervalId);
  }, [workspaceMedia]);

  // Poll processing/pending Seedance items
  useEffect(() => {
    const pendingItems = seedanceMedia.filter((m) => SEEDANCE_PENDING_STATES.includes(m.state));
    if (pendingItems.length === 0) return;

    const intervalId = setInterval(async () => {
      for (const item of pendingItems) {
        try {
          const response = await getSeedanceTask(item.taskId);
          const updated = response.data;
          if (updated.state !== item.state) {
            setSeedanceMedia((prev) => prev.map((m) => (m._id === item._id ? updated : m)));
            setSelectedMedia((prev) => (prev?._id === item._id ? updated : prev));
          }
        } catch (error) {
          console.error("Error polling seedance item:", error);
        }
      }
    }, POLL_INTERVAL);

    return () => clearInterval(intervalId);
  }, [seedanceMedia]);

  // Fetch Veo3 workspace media on mount
  useEffect(() => {
    if (!currentWorkspace) return;
    const fetchVeo3Media = async () => {
      setIsLoadingVeo3Media(true);
      try {
        const response = await getVeo3Tasks({
          workspaceId: currentWorkspace._id,
        });
        if (response.success) {
          setVeo3Media(response.data);
        }
      } catch (error) {
        console.error("Error fetching veo3 media:", error);
      } finally {
        setIsLoadingVeo3Media(false);
      }
    };
    fetchVeo3Media();
  }, [currentWorkspace]);

  // Poll processing/pending Veo3 items (same pattern as Sora2/Seedance)
  useEffect(() => {
    const pendingItems = veo3Media.filter(
      (m) => VEO3_PENDING_STATES.includes(m.state) || m.fourKState === "pending" || m.fourKState === "processing"
    );
    if (pendingItems.length === 0) return;

    const intervalId = setInterval(async () => {
      for (const item of pendingItems) {
        try {
          const response = await getVeo3Task(item.taskId);
          const updated = response.data;
          if (updated.state !== item.state || updated.fourKState !== item.fourKState) {
            setVeo3Media((prev) => prev.map((m) => (m._id === item._id ? updated : m)));
            setSelectedMedia((prev) => (prev?._id === item._id ? updated : prev));
          }
        } catch (error) {
          console.error("Error polling veo3 item:", error);
        }
      }
    }, POLL_INTERVAL);

    return () => clearInterval(intervalId);
  }, [veo3Media]);

  // Fetch Nano Banana workspace media on mount
  useEffect(() => {
    if (!currentWorkspace) return;
    const fetchNanoBananaMedia = async () => {
      setIsLoadingNanoBananaMedia(true);
      try {
        const response = await getNanoBananaTasks({
          workspaceId: currentWorkspace._id,
        });
        if (response.success) {
          setNanoBananaMedia(response.data);
        }
      } catch (error) {
        console.error("Error fetching nano banana media:", error);
      } finally {
        setIsLoadingNanoBananaMedia(false);
      }
    };
    fetchNanoBananaMedia();
  }, [currentWorkspace]);

  // Poll processing/pending Nano Banana items
  useEffect(() => {
    const pendingItems = nanoBananaMedia.filter(
      (m) => m.state === "pending" || m.state === "processing"
    );
    if (pendingItems.length === 0) return;

    const intervalId = setInterval(async () => {
      for (const item of pendingItems) {
        try {
          const response = await getNanoBananaTask(item.taskId);
          const updated = response.data;
          if (updated.state !== item.state) {
            setNanoBananaMedia((prev) => prev.map((m) => (m._id === item._id ? updated : m)));
            setSelectedMedia((prev) => (prev?._id === item._id ? updated : prev));
          }
        } catch (error) {
          console.error("Error polling nano banana item:", error);
        }
      }
    }, POLL_INTERVAL);

    return () => clearInterval(intervalId);
  }, [nanoBananaMedia]);

  // Fetch Gemini workspace media on mount
  useEffect(() => {
    if (!currentWorkspace) return;
    const fetchGeminiMedia = async () => {
      setIsLoadingGeminiMedia(true);
      try {
        const response = await getGeminiTasks({
          workspaceId: currentWorkspace._id,
        });
        if (response.success) {
          setGeminiMedia(response.data);
        }
      } catch (error) {
        console.error("Error fetching gemini media:", error);
      } finally {
        setIsLoadingGeminiMedia(false);
      }
    };
    fetchGeminiMedia();
  }, [currentWorkspace]);

  // Poll processing/pending Gemini items (same waiting/queuing/generating pattern as Seedance)
  useEffect(() => {
    const pendingItems = geminiMedia.filter((m) => GEMINI_PENDING_STATES.includes(m.state));
    if (pendingItems.length === 0) return;

    const intervalId = setInterval(async () => {
      for (const item of pendingItems) {
        try {
          const response = await getGeminiTask(item.taskId);
          const updated = response.data;
          if (updated.state !== item.state) {
            setGeminiMedia((prev) => prev.map((m) => (m._id === item._id ? updated : m)));
            setSelectedMedia((prev) => (prev?._id === item._id ? updated : prev));
          }
        } catch (error) {
          console.error("Error polling gemini item:", error);
        }
      }
    }, POLL_INTERVAL);

    return () => clearInterval(intervalId);
  }, [geminiMedia]);

  // Fetch Wan 3.0 workspace media on mount
  useEffect(() => {
    if (!currentWorkspace) return;
    const fetchWan3Media = async () => {
      setIsLoadingWan3Media(true);
      try {
        const response = await getWan3Tasks({
          workspaceId: currentWorkspace._id,
        });
        if (response.success) {
          setWan3Media(response.data);
        }
      } catch (error) {
        console.error("Error fetching wan3 media:", error);
      } finally {
        setIsLoadingWan3Media(false);
      }
    };
    fetchWan3Media();
  }, [currentWorkspace]);

  // Poll processing/pending Wan 3.0 items (same waiting/queuing/generating pattern as Gemini/Seedance)
  useEffect(() => {
    const pendingItems = wan3Media.filter((m) => WAN3_PENDING_STATES.includes(m.state));
    if (pendingItems.length === 0) return;

    const intervalId = setInterval(async () => {
      for (const item of pendingItems) {
        try {
          const response = await getWan3Task(item.taskId);
          const updated = response.data;
          if (updated.state !== item.state) {
            setWan3Media((prev) => prev.map((m) => (m._id === item._id ? updated : m)));
            setSelectedMedia((prev) => (prev?._id === item._id ? updated : prev));
          }
        } catch (error) {
          console.error("Error polling wan3 item:", error);
        }
      }
    }, POLL_INTERVAL);

    return () => clearInterval(intervalId);
  }, [wan3Media]);

  // Fetch Hetzner (Local LLM Engine) workspace media on mount
  useEffect(() => {
    if (!currentWorkspace) return;
    const fetchHetznerMedia = async () => {
      setIsLoadingHetznerMedia(true);
      try {
        const response = await getHetznerTasks({ workspaceId: currentWorkspace._id });
        if (response.success) {
          setHetznerMedia(response.data);
        }
      } catch (error) {
        console.error("Error fetching hetzner media:", error);
      } finally {
        setIsLoadingHetznerMedia(false);
      }
    };
    fetchHetznerMedia();
  }, [currentWorkspace]);

  // Poll processing/pending Hetzner items
  useEffect(() => {
    const pendingItems = hetznerMedia.filter((m) => HETZNER_PENDING_STATES.includes(m.state));
    if (pendingItems.length === 0) return;

    const intervalId = setInterval(async () => {
      for (const item of pendingItems) {
        try {
          const response = await getHetznerTask(item.taskId);
          const updated = response.data;
          if (updated.state !== item.state) {
            setHetznerMedia((prev) => prev.map((m) => (m._id === item._id ? updated : m)));
            setSelectedMedia((prev) => (prev?._id === item._id ? updated : prev));
          }
        } catch (error) {
          console.error("Error polling hetzner item:", error);
        }
      }
    }, POLL_INTERVAL);

    return () => clearInterval(intervalId);
  }, [hetznerMedia]);

  const pollForSora2Result = useCallback(
    async (taskId: string): Promise<Sora2Media | null> => {
      let attempts = 0;
      while (attempts < MAX_POLL_ATTEMPTS) {
        attempts++;
        try {
          const response = await getSora2Task(taskId);
          if (response.data.state === "success") {
            return response.data;
          }
          if (response.data.state === "fail") {
            addNotification(response.data.failMsg || "Video generation failed", "error");
            return null;
          }
        } catch (error) {
          console.error("Error polling task:", error);
        }
        await new Promise((resolve) => setTimeout(resolve, POLL_INTERVAL));
      }
      addNotification("Generation timed out. Check back later.", "warning");
      return null;
    },
    [addNotification]
  );

  const pollForSeedanceResult = useCallback(
    async (taskId: string): Promise<SeedanceMedia | null> => {
      let attempts = 0;
      while (attempts < MAX_POLL_ATTEMPTS) {
        attempts++;
        try {
          const response = await getSeedanceTask(taskId);
          if (response.data.state === "success") {
            return response.data;
          }
          if (response.data.state === "fail") {
            addNotification(response.data.failMsg || "Video generation failed", "error");
            return null;
          }
        } catch (error) {
          console.error("Error polling seedance task:", error);
        }
        await new Promise((resolve) => setTimeout(resolve, POLL_INTERVAL));
      }
      addNotification("Generation timed out. Check back later.", "warning");
      return null;
    },
    [addNotification]
  );

  const pollForVeo3Result = useCallback(
    async (taskId: string): Promise<Veo3Media | null> => {
      let attempts = 0;
      while (attempts < MAX_POLL_ATTEMPTS) {
        attempts++;
        try {
          const response = await getVeo3Task(taskId);
          if (response.data.state === "success") {
            return response.data;
          }
          if (response.data.state === "fail") {
            addNotification(
              response.data.failMsg || response.data.errorMessage || "Video generation failed",
              "error"
            );
            return null;
          }
        } catch (error) {
          console.error("Error polling veo3 task:", error);
        }
        await new Promise((resolve) => setTimeout(resolve, POLL_INTERVAL));
      }
      addNotification("Generation timed out. Check back later.", "warning");
      return null;
    },
    [addNotification]
  );

  const pollForNanoBananaResult = useCallback(
    async (taskId: string): Promise<NanoBananaMedia | null> => {
      let attempts = 0;
      while (attempts < MAX_POLL_ATTEMPTS) {
        attempts++;
        try {
          const response = await getNanoBananaTask(taskId);
          if (response.data.state === "success") {
            return response.data;
          }
          if (response.data.state === "fail") {
            addNotification(response.data.failMsg || "Image generation failed", "error");
            return null;
          }
        } catch (error) {
          console.error("Error polling nano banana task:", error);
        }
        await new Promise((resolve) => setTimeout(resolve, POLL_INTERVAL));
      }
      addNotification("Generation timed out. Check back later.", "warning");
      return null;
    },
    [addNotification]
  );

  const pollForGeminiResult = useCallback(
    async (taskId: string): Promise<GeminiMedia | null> => {
      let attempts = 0;
      while (attempts < MAX_POLL_ATTEMPTS) {
        attempts++;
        try {
          const response = await getGeminiTask(taskId);
          if (response.data.state === "success") {
            return response.data;
          }
          if (response.data.state === "fail") {
            addNotification(response.data.failMsg || "Video generation failed", "error");
            return null;
          }
        } catch (error) {
          console.error("Error polling gemini task:", error);
        }
        await new Promise((resolve) => setTimeout(resolve, POLL_INTERVAL));
      }
      addNotification("Generation timed out. Check back later.", "warning");
      return null;
    },
    [addNotification]
  );

  const pollForWan3Result = useCallback(
    async (taskId: string): Promise<Wan3Media | null> => {
      let attempts = 0;
      while (attempts < MAX_POLL_ATTEMPTS) {
        attempts++;
        try {
          const response = await getWan3Task(taskId);
          if (response.data.state === "success") {
            return response.data;
          }
          if (response.data.state === "fail") {
            addNotification(response.data.failMsg || "Video generation failed", "error");
            return null;
          }
        } catch (error) {
          console.error("Error polling wan3 task:", error);
        }
        await new Promise((resolve) => setTimeout(resolve, POLL_INTERVAL));
      }
      addNotification("Generation timed out. Check back later.", "warning");
      return null;
    },
    [addNotification]
  );

  const pollForHetznerResult = useCallback(
    async (taskId: string): Promise<HetznerMedia | null> => {
      let attempts = 0;
      while (attempts < MAX_POLL_ATTEMPTS) {
        attempts++;
        try {
          const response = await getHetznerTask(taskId);
          if (response.data.state === "success") {
            return response.data;
          }
          if (response.data.state === "fail") {
            addNotification(response.data.failMsg || "Video generation failed", "error");
            return null;
          }
        } catch (error) {
          console.error("Error polling hetzner task:", error);
        }
        await new Promise((resolve) => setTimeout(resolve, POLL_INTERVAL));
      }
      addNotification("Generation timed out. Check back later.", "warning");
      return null;
    },
    [addNotification]
  );

  // Shot helpers
  const updateShot = (index: number, field: keyof Sora2Shot, value: string | number) => {
    setShots((prev) => prev.map((shot, i) => (i === index ? { ...shot, [field]: value } : shot)));
  };

  const addShot = () => {
    setShots((prev) => [...prev, { ...DEFAULT_SHOT }]);
  };

  const removeShot = (index: number) => {
    if (shots.length <= 1) return;
    setShots((prev) => prev.filter((_, i) => i !== index));
  };

  const totalShotDuration = shots.reduce((sum, s) => sum + s.duration, 0);
  const maxDuration = parseInt(nFrames, 10);

  const canSubmit = () => {
    if (isGenerating) return false;
    if (modelMode === "storyboard") {
      const hasScene = shots.some((s) => s.Scene.trim().length > 0);
      return hasScene && totalShotDuration <= maxDuration;
    }
    return prompt.trim().length > 0;
  };

  const handleSubmit = async () => {
    if (!currentWorkspace) {
      addNotification("No workspace selected", "error");
      return;
    }

    // Nano Banana generation (prompt-to-image)
    if (modelMode === "nanobanana") {
      if (!prompt.trim()) {
        addNotification("Please describe the image you want to generate", "error");
        return;
      }

      let imageInput = uploadedImages.map((img) => img.publicUrl);
      if (imageInput.length > 14) {
        addNotification(
          "Nano Banana supports up to 14 reference images. Extra images will be ignored.",
          "warning"
        );
        imageInput = imageInput.slice(0, 14);
      }

      setIsGenerating(true);
      try {
        for (let i = 0; i < quantity; i++) {
          const response = await createNanoBanana({
            prompt: prompt.trim(),
            ...(imageInput.length > 0 && { imageInput }),
            aspectRatio: nanoBananaAspectRatio,
            resolution: nanoBananaResolution,
            outputFormat: nanoBananaOutputFormat,
            workspaceId: currentWorkspace._id,
            userId: user?._id,
          });

          if (response.success) {
            addNotification("Nano Banana image generation started!", "info");
            const newMedia = response.data.nanoBananaMedia;
            setCurrentGenMedia(newMedia);
            setNanoBananaMedia((prev) => [newMedia, ...prev]);

            const result = await pollForNanoBananaResult(response.data.taskId);
            if (result) {
              setCurrentGenMedia(result);
              setNanoBananaMedia((prev) => prev.map((m) => (m._id === newMedia._id ? result : m)));
              addNotification("Nano Banana image generated!", "success");
            } else {
              setCurrentGenMedia(null);
              setNanoBananaMedia((prev) =>
                prev.map((m) => (m._id === newMedia._id ? { ...m, state: "fail" as const } : m))
              );
            }
          }
        }
      } catch (error: any) {
        console.error("Error generating nano banana image:", error);
        addNotification(
          error.response?.data?.message || "Failed to start Nano Banana generation",
          "error"
        );
      } finally {
        setIsGenerating(false);
      }
      return;
    }

    // Gemini Omni 1.1 Flash generation
    if (modelMode === "gemini") {
      if (!prompt.trim()) {
        addNotification("Please describe what you want to generate", "error");
        return;
      }

      const imageUrls = uploadedImages.map((img) => img.publicUrl);

      setIsGenerating(true);
      try {
        for (let i = 0; i < quantity; i++) {
          const response = await createGeminiGenerate({
            prompt: prompt.trim(),
            ...(imageUrls.length > 0 && { imageUrls }),
            duration: geminiDuration,
            aspectRatio: geminiAspectRatio,
            resolution: geminiResolution,
            seed: geminiSeed,
            workspaceId: currentWorkspace._id,
            userId: user?._id,
          });

          if (response.success) {
            addNotification("Gemini generation started!", "info");
            const newMedia = response.data.geminiMedia;
            setCurrentGenMedia(newMedia);
            setGeminiMedia((prev) => [newMedia, ...prev]);

            const result = await pollForGeminiResult(response.data.taskId);
            if (result) {
              setCurrentGenMedia(result);
              setGeminiMedia((prev) => prev.map((m) => (m._id === newMedia._id ? result : m)));
              addNotification("Gemini video generated!", "success");
            } else {
              setCurrentGenMedia(null);
              setGeminiMedia((prev) =>
                prev.map((m) => (m._id === newMedia._id ? { ...m, state: "fail" as const } : m))
              );
            }
          }
        }
      } catch (error: any) {
        console.error("Error generating gemini video:", error);
        addNotification(
          error.response?.data?.message || "Failed to start Gemini generation",
          "error"
        );
      } finally {
        setIsGenerating(false);
      }
      return;
    }

    // Wan 3.0 Video generation (base and Prime share the same request/response shape)
    if (modelMode === "wan3" || modelMode === "wan3prime") {
      if (!prompt.trim()) {
        addNotification("Please describe what you want to generate", "error");
        return;
      }

      // Same image-count-driven mode logic as Seedance 2.5:
      //  0 images → text-to-video
      //  1 image  → image-to-video (first frame)
      //  2 images → image-to-video (first + last frame)
      //  3+ images → multimodal reference (up to 10)
      const imageUrls = uploadedImages.map((img) => img.publicUrl);
      const imageMode: "text" | "first-frame" | "first-last-frame" | "multimodal" =
        imageUrls.length === 0
          ? "text"
          : imageUrls.length === 1
            ? "first-frame"
            : imageUrls.length === 2
              ? "first-last-frame"
              : "multimodal";

      if (imageUrls.length > 10) {
        addNotification(
          "Wan 3.0 supports up to 10 reference images total. Please remove extras.",
          "error"
        );
        return;
      }

      setIsGenerating(true);
      try {
        const createWan3 = modelMode === "wan3prime" ? createWan3PrimeGenerate : createWan3Generate;
        for (let i = 0; i < quantity; i++) {
          const response = await createWan3({
            prompt: prompt.trim(),
            workspaceId: currentWorkspace._id,
            userId: user?._id,
            ...(imageMode === "first-frame" && { firstFrameUrl: imageUrls[0] }),
            ...(imageMode === "first-last-frame" && {
              firstFrameUrl: imageUrls[0],
              lastFrameUrl: imageUrls[1],
            }),
            ...(imageMode === "multimodal" && { referenceImageUrls: imageUrls }),
            resolution: wan3Resolution,
            aspectRatio: wan3AspectRatio,
            duration: wan3Duration,
            audio: wan3Audio,
            seed: wan3Seed,
            nsfwChecker: wan3NsfwChecker,
          });

          if (response.success) {
            addNotification(
              modelMode === "wan3prime" ? "Wan 3.0 Prime generation started!" : "Wan 3.0 generation started!",
              "info"
            );
            const newMedia = response.data.wan3Media;
            setCurrentGenMedia(newMedia);
            setWan3Media((prev) => [newMedia, ...prev]);

            const result = await pollForWan3Result(response.data.taskId);
            if (result) {
              setCurrentGenMedia(result);
              setWan3Media((prev) => prev.map((m) => (m._id === newMedia._id ? result : m)));
              addNotification("Wan 3.0 video generated!", "success");
            } else {
              setCurrentGenMedia(null);
              setWan3Media((prev) =>
                prev.map((m) => (m._id === newMedia._id ? { ...m, state: "fail" as const } : m))
              );
            }
          }
        }
      } catch (error: any) {
        console.error("Error generating wan3 video:", error);
        addNotification(
          error.response?.data?.message || "Failed to start Wan 3.0 generation",
          "error"
        );
      } finally {
        setIsGenerating(false);
      }
      return;
    }

    // Local LLM Engine (self-hosted MiniMax H3 on Hetzner) — text-to-video only, no image inputs
    if (modelMode === "hetzner") {
      if (!prompt.trim()) {
        addNotification("Please describe what you want to generate", "error");
        return;
      }

      setIsGenerating(true);
      try {
        for (let i = 0; i < quantity; i++) {
          const response = await createHetznerGenerate({
            prompt: prompt.trim(),
            durationS: hetznerDurationS,
            resolution: hetznerResolution,
            workspaceId: currentWorkspace._id,
            userId: user?._id,
          });

          if (response.success) {
            addNotification("Local LLM Engine generation started!", "info");
            const newMedia = response.data.hetznerMedia;
            setCurrentGenMedia(newMedia);
            setHetznerMedia((prev) => [newMedia, ...prev]);

            const result = await pollForHetznerResult(response.data.taskId);
            if (result) {
              setCurrentGenMedia(result);
              setHetznerMedia((prev) => prev.map((m) => (m._id === newMedia._id ? result : m)));
              addNotification("Local LLM Engine video generated!", "success");
            } else {
              setCurrentGenMedia(null);
              setHetznerMedia((prev) =>
                prev.map((m) => (m._id === newMedia._id ? { ...m, state: "fail" as const } : m))
              );
            }
          }
        }
      } catch (error: any) {
        console.error("Error generating hetzner video:", error);
        addNotification(
          error.response?.data?.message || "Failed to start Local LLM Engine generation",
          "error"
        );
      } finally {
        setIsGenerating(false);
      }
      return;
    }

    // Veo3 generation
    if (modelMode === "veo3") {
      if (!prompt.trim()) {
        addNotification("Please describe what you want to generate", "error");
        return;
      }

      setIsGenerating(true);
      try {
        for (let i = 0; i < quantity; i++) {
          let generationType:
            | "TEXT_2_VIDEO"
            | "FIRST_AND_LAST_FRAMES_2_VIDEO"
            | "REFERENCE_2_VIDEO" = "TEXT_2_VIDEO";
          if (uploadedImages.length > 0) {
            generationType = veo3ImageGenType;
          }

          const response = await createVeo3Generate({
            prompt: prompt.trim(),
            imageUrls:
              uploadedImages.length > 0 ? uploadedImages.map((img) => img.publicUrl) : undefined,
            model: veo3Model,
            generationType,
            aspectRatio: veo3AspectRatio,
            seeds: veo3Seeds,
            enableTranslation: veo3EnableTranslation || undefined,
            watermark: veo3Watermark || undefined,
            workspaceId: currentWorkspace._id,
            userId: user?._id,
          });

          if (response.success) {
            addNotification("Veo3 generation started!", "info");
            const newMedia = response.data.veo3Media;
            setCurrentGenMedia(newMedia);
            setVeo3Media((prev) => [newMedia, ...prev]);

            const result = await pollForVeo3Result(response.data.taskId);
            if (result) {
              setCurrentGenMedia(result);
              setVeo3Media((prev) => prev.map((m) => (m._id === newMedia._id ? result : m)));
              addNotification("Veo3 video generated!", "success");
            } else {
              setCurrentGenMedia(null);
              setVeo3Media((prev) =>
                prev.map((m) => (m._id === newMedia._id ? { ...m, state: "fail" as const } : m))
              );
            }
          }
        }
      } catch (error: any) {
        console.error("Error generating veo3 video:", error);
        addNotification(
          error.response?.data?.message || "Failed to start Veo3 generation",
          "error"
        );
      } finally {
        setIsGenerating(false);
      }
      return;
    }

    // Seedance 2.0 Fast generation
    if (modelMode === "seedance2fast") {
      if (!prompt.trim()) {
        addNotification("Please describe what you want to generate", "error");
        return;
      }

      // Fast mode does not support firstFrame/lastFrame — any uploaded images go
      // into referenceImageUrls only (multimodal reference).
      const imageUrls = uploadedImages.map((img) => img.publicUrl);

      if (imageUrls.length > 9) {
        addNotification(
          "Seedance 2.0 Fast supports up to 9 reference images. Please remove extras.",
          "error"
        );
        return;
      }

      setIsGenerating(true);
      try {
        for (let i = 0; i < quantity; i++) {
          const response = await createSeedance2Fast({
            prompt: prompt.trim(),
            workspaceId: currentWorkspace._id,
            userId: user?._id,
            ...(imageUrls.length > 0 && { referenceImageUrls: imageUrls }),
            aspectRatio: seedance2FastAspectRatio,
            resolution: seedance2FastResolution,
            duration: seedance2FastDuration,
            generateAudio: seedance2FastGenerateAudio,
            webSearch: seedance2FastWebSearch,
            nsfwChecker: seedance2FastNsfwChecker,
          });

          if (response.success) {
            addNotification("Seedance 2.0 Fast generation started!", "info");
            const newMedia = response.data.seedanceMedia;
            setCurrentGenMedia(newMedia);
            setSeedanceMedia((prev) => [newMedia, ...prev]);

            const result = await pollForSeedanceResult(response.data.taskId);
            if (result) {
              setCurrentGenMedia(result);
              setSeedanceMedia((prev) => prev.map((m) => (m._id === newMedia._id ? result : m)));
              addNotification("Seedance 2.0 Fast video generated!", "success");
            } else {
              setCurrentGenMedia(null);
              setSeedanceMedia((prev) =>
                prev.map((m) => (m._id === newMedia._id ? { ...m, state: "fail" as const } : m))
              );
            }
          }
        }
      } catch (error: any) {
        console.error("Error generating seedance 2.0 fast video:", error);
        addNotification(
          error.response?.data?.message || "Failed to start Seedance 2.0 Fast generation",
          "error"
        );
      } finally {
        setIsGenerating(false);
      }
      return;
    }

    // Seedance 2.0 generation
    if (modelMode === "seedance2") {
      if (!prompt.trim()) {
        addNotification("Please describe what you want to generate", "error");
        return;
      }

      // Map uploadedImages to the 2.0 input mode:
      //  0 images → text-to-video
      //  1 image  → image-to-video (first frame)
      //  2 images → image-to-video (first + last frame)
      //  3+ images → multimodal reference (up to 9)
      const imageUrls = uploadedImages.map((img) => img.publicUrl);
      const imageMode: "text" | "first-frame" | "first-last-frame" | "multimodal" =
        imageUrls.length === 0
          ? "text"
          : imageUrls.length === 1
            ? "first-frame"
            : imageUrls.length === 2
              ? "first-last-frame"
              : "multimodal";

      if (imageUrls.length > 9) {
        addNotification(
          "Seedance 2.0 supports up to 9 reference images total. Please remove extras.",
          "error"
        );
        return;
      }

      setIsGenerating(true);
      try {
        for (let i = 0; i < quantity; i++) {
          const response = await createSeedance2({
            prompt: prompt.trim(),
            workspaceId: currentWorkspace._id,
            userId: user?._id,
            ...(imageMode === "first-frame" && { firstFrameUrl: imageUrls[0] }),
            ...(imageMode === "first-last-frame" && {
              firstFrameUrl: imageUrls[0],
              lastFrameUrl: imageUrls[1],
            }),
            ...(imageMode === "multimodal" && { referenceImageUrls: imageUrls }),
            // Always send aspect ratio — backend honors it for both text and image modes
            aspectRatio: seedance2AspectRatio,
            resolution: seedance2Resolution,
            duration: seedance2Duration,
            generateAudio: seedance2GenerateAudio,
            webSearch: seedance2WebSearch,
            nsfwChecker: seedance2NsfwChecker,
          });

          if (response.success) {
            addNotification("Seedance 2.0 generation started!", "info");
            const newMedia = response.data.seedanceMedia;
            setCurrentGenMedia(newMedia);
            setSeedanceMedia((prev) => [newMedia, ...prev]);

            const result = await pollForSeedanceResult(response.data.taskId);
            if (result) {
              setCurrentGenMedia(result);
              setSeedanceMedia((prev) => prev.map((m) => (m._id === newMedia._id ? result : m)));
              addNotification("Seedance 2.0 video generated!", "success");
            } else {
              setCurrentGenMedia(null);
              setSeedanceMedia((prev) =>
                prev.map((m) => (m._id === newMedia._id ? { ...m, state: "fail" as const } : m))
              );
            }
          }
        }
      } catch (error: any) {
        console.error("Error generating seedance 2.0 video:", error);
        addNotification(
          error.response?.data?.message || "Failed to start Seedance 2.0 generation",
          "error"
        );
      } finally {
        setIsGenerating(false);
      }
      return;
    }

    // Seedance 2.5 generation
    if (modelMode === "seedance25") {
      if (!prompt.trim()) {
        addNotification("Please describe what you want to generate", "error");
        return;
      }

      // Same image-count-driven mode logic as Seedance 2.0:
      //  0 images → text-to-video
      //  1 image  → image-to-video (first frame)
      //  2 images → image-to-video (first + last frame)
      //  3+ images → multimodal reference (up to 9)
      const imageUrls = uploadedImages.map((img) => img.publicUrl);
      const imageMode: "text" | "first-frame" | "first-last-frame" | "multimodal" =
        imageUrls.length === 0
          ? "text"
          : imageUrls.length === 1
            ? "first-frame"
            : imageUrls.length === 2
              ? "first-last-frame"
              : "multimodal";

      if (imageUrls.length > 9) {
        addNotification(
          "Seedance 2.5 supports up to 9 reference images total. Please remove extras.",
          "error"
        );
        return;
      }

      setIsGenerating(true);
      try {
        for (let i = 0; i < quantity; i++) {
          const response = await createSeedance25({
            prompt: prompt.trim(),
            workspaceId: currentWorkspace._id,
            userId: user?._id,
            ...(imageMode === "first-frame" && { firstFrameUrl: imageUrls[0] }),
            ...(imageMode === "first-last-frame" && {
              firstFrameUrl: imageUrls[0],
              lastFrameUrl: imageUrls[1],
            }),
            ...(imageMode === "multimodal" && { referenceImageUrls: imageUrls }),
            // Seedance 2.5 rejects first-frame / first-last-frame tasks unless
            // aspect_ratio is 'adaptive' (backend also enforces this as a safety net).
            aspectRatio:
              imageMode === "first-frame" || imageMode === "first-last-frame"
                ? "adaptive"
                : seedance25AspectRatio,
            resolution: seedance25Resolution,
            duration: seedance25Duration,
            generateAudio: seedance25GenerateAudio,
            returnLastFrame: seedance25ReturnLastFrame,
            outputFormat: seedance25OutputFormat,
            webSearch: seedance25WebSearch,
            nsfwChecker: seedance25NsfwChecker,
          });

          if (response.success) {
            addNotification("Seedance 2.5 generation started!", "info");
            const newMedia = response.data.seedanceMedia;
            setCurrentGenMedia(newMedia);
            setSeedanceMedia((prev) => [newMedia, ...prev]);

            const result = await pollForSeedanceResult(response.data.taskId);
            if (result) {
              setCurrentGenMedia(result);
              setSeedanceMedia((prev) => prev.map((m) => (m._id === newMedia._id ? result : m)));
              addNotification("Seedance 2.5 video generated!", "success");
            } else {
              setCurrentGenMedia(null);
              setSeedanceMedia((prev) =>
                prev.map((m) => (m._id === newMedia._id ? { ...m, state: "fail" as const } : m))
              );
            }
          }
        }
      } catch (error: any) {
        console.error("Error generating seedance 2.5 video:", error);
        addNotification(
          error.response?.data?.message || "Failed to start Seedance 2.5 generation",
          "error"
        );
      } finally {
        setIsGenerating(false);
      }
      return;
    }

    // Seedance 1.5 Pro generation
    if (modelMode === "seedance15pro") {
      if (!prompt.trim()) {
        addNotification("Please describe what you want to generate", "error");
        return;
      }

      setIsGenerating(true);
      try {
        for (let i = 0; i < quantity; i++) {
          const response = await createSeedance15Pro({
            prompt: prompt.trim(),
            inputUrls:
              uploadedImages.length > 0 ? uploadedImages.map((img) => img.publicUrl) : undefined,
            aspectRatio: uploadedImages.length === 0 ? seedanceAspectRatio : undefined,
            resolution: seedanceResolution,
            duration: seedance15Duration,
            fixedLens: fixedLens || undefined,
            generateAudio: generateAudio || undefined,
            workspaceId: currentWorkspace._id,
            userId: user?._id,
          });

          if (response.success) {
            addNotification("Seedance 1.5 Pro generation started!", "info");
            const newMedia = response.data.seedanceMedia;
            setCurrentGenMedia(newMedia);
            setSeedanceMedia((prev) => [newMedia, ...prev]);

            const result = await pollForSeedanceResult(response.data.taskId);
            if (result) {
              setCurrentGenMedia(result);
              setSeedanceMedia((prev) => prev.map((m) => (m._id === newMedia._id ? result : m)));
              addNotification("Seedance 1.5 Pro video generated!", "success");
            } else {
              setCurrentGenMedia(null);
              setSeedanceMedia((prev) =>
                prev.map((m) => (m._id === newMedia._id ? { ...m, state: "fail" as const } : m))
              );
            }
          }
        }
      } catch (error: any) {
        console.error("Error generating seedance 1.5 pro video:", error);
        addNotification(
          error.response?.data?.message || "Failed to start Seedance 1.5 Pro generation",
          "error"
        );
      } finally {
        setIsGenerating(false);
      }
      return;
    }

    // Seedance generation
    if (modelMode === "seedance") {
      if (!prompt.trim()) {
        addNotification("Please describe what you want to generate", "error");
        return;
      }

      setIsGenerating(true);
      try {
        for (let i = 0; i < quantity; i++) {
          let response: Awaited<ReturnType<typeof createSeedanceTextToVideo>>;

          if (uploadedImages.length > 0) {
            response = await createSeedanceImageToVideo({
              prompt: prompt.trim(),
              imageUrl: uploadedImages[0].publicUrl,
              resolution: seedanceResolution,
              duration: seedanceDuration,
              workspaceId: currentWorkspace._id,
              userId: user?._id,
            });
          } else {
            response = await createSeedanceTextToVideo({
              prompt: prompt.trim(),
              aspectRatio: seedanceAspectRatio,
              resolution: seedanceResolution,
              duration: seedanceDuration,
              workspaceId: currentWorkspace._id,
              userId: user?._id,
            });
          }

          if (response.success) {
            addNotification("Seedance generation started!", "info");
            const newMedia = response.data.seedanceMedia;
            setCurrentGenMedia(newMedia);
            setSeedanceMedia((prev) => [newMedia, ...prev]);

            const result = await pollForSeedanceResult(response.data.taskId);
            if (result) {
              setCurrentGenMedia(result);
              setSeedanceMedia((prev) => prev.map((m) => (m._id === newMedia._id ? result : m)));
              addNotification("Seedance video generated!", "success");
            } else {
              setCurrentGenMedia(null);
              setSeedanceMedia((prev) =>
                prev.map((m) => (m._id === newMedia._id ? { ...m, state: "fail" as const } : m))
              );
            }
          }
        }
      } catch (error: any) {
        console.error("Error generating seedance video:", error);
        addNotification(
          error.response?.data?.message || "Failed to start Seedance generation",
          "error"
        );
      } finally {
        setIsGenerating(false);
      }
      return;
    }

    // Storyboard generation
    if (modelMode === "storyboard") {
      const validShots = shots.filter((s) => s.Scene.trim());
      if (validShots.length === 0) {
        addNotification("Please describe at least one scene", "error");
        return;
      }

      setIsGenerating(true);
      try {
        const response = await createSora2Storyboard({
          shots: validShots.map((s) => ({
            Scene: s.Scene.trim(),
            duration: s.duration,
          })),
          imageUrls:
            uploadedImages.length > 0 ? uploadedImages.map((img) => img.publicUrl) : undefined,
          aspectRatio,
          nFrames,
          workspaceId: currentWorkspace._id,
          userId: user?._id,
        });

        if (response.success) {
          addNotification("Storyboard generation started!", "info");
          const newMedia = response.data.sora2Media;
          setCurrentGenMedia(newMedia);
          setWorkspaceMedia((prev) => [newMedia, ...prev]);

          const result = await pollForSora2Result(response.data.taskId);
          if (result) {
            setCurrentGenMedia(result);
            setWorkspaceMedia((prev) => prev.map((m) => (m._id === newMedia._id ? result : m)));
            addNotification("Storyboard video generated!", "success");
          } else {
            setCurrentGenMedia(null);
            setWorkspaceMedia((prev) =>
              prev.map((m) => (m._id === newMedia._id ? { ...m, state: "fail" as const } : m))
            );
          }
        }
      } catch (error: any) {
        console.error("Error generating storyboard:", error);
        addNotification(
          error.response?.data?.message || "Failed to start storyboard generation",
          "error"
        );
      } finally {
        setIsGenerating(false);
      }
      return;
    }

    // Standard & Pro Sora2 text-to-video / image-to-video
    if (!prompt.trim()) {
      addNotification("Please describe what you want to generate", "error");
      return;
    }

    setIsGenerating(true);
    const isPro = modelMode === "sora2pro";

    try {
      for (let i = 0; i < quantity; i++) {
        let response: Awaited<ReturnType<typeof createSora2TextToVideo>>;

        if (isPro) {
          if (uploadedImages.length > 0) {
            response = await createSora2ProImageToVideo({
              prompt: prompt.trim(),
              imageUrls: uploadedImages.map((img) => img.publicUrl),
              aspectRatio,
              nFrames,
              size,
              workspaceId: currentWorkspace._id,
              userId: user?._id,
            });
          } else {
            response = await createSora2ProTextToVideo({
              prompt: prompt.trim(),
              imageUrls:
                uploadedImages.length > 0 ? uploadedImages.map((img) => img.publicUrl) : undefined,
              aspectRatio,
              nFrames,
              size,
              workspaceId: currentWorkspace._id,
              userId: user?._id,
            });
          }
        } else {
          if (uploadedImages.length > 0) {
            response = await createSora2ImageToVideo({
              prompt: prompt.trim(),
              imageUrls: uploadedImages.map((img) => img.publicUrl),
              aspectRatio,
              nFrames,
              workspaceId: currentWorkspace._id,
              userId: user?._id,
            });
          } else {
            response = await createSora2TextToVideo({
              prompt: prompt.trim(),
              aspectRatio,
              nFrames,
              workspaceId: currentWorkspace._id,
              userId: user?._id,
            });
          }
        }

        if (response.success) {
          addNotification("Video generation started!", "info");

          const newMedia = response.data.sora2Media;

          // Show as current generation and add to carousel immediately
          setCurrentGenMedia(newMedia);
          setWorkspaceMedia((prev) => [newMedia, ...prev]);

          // Poll for completion
          const result = await pollForSora2Result(response.data.taskId);
          if (result) {
            setCurrentGenMedia(result);
            setWorkspaceMedia((prev) => prev.map((m) => (m._id === newMedia._id ? result : m)));
            addNotification("Video generated successfully!", "success");
          } else {
            setCurrentGenMedia(null);
            setWorkspaceMedia((prev) =>
              prev.map((m) => (m._id === newMedia._id ? { ...m, state: "fail" as const } : m))
            );
          }
        }
      }
    } catch (error: any) {
      console.error("Error generating video:", error);
      addNotification(error.response?.data?.message || "Failed to start generation", "error");
    } finally {
      setIsGenerating(false);
    }
  };

  const handleImageUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (!files || !currentWorkspace || !user) return;

    setIsUploadingImage(true);
    try {
      for (const file of Array.from(files)) {
        const previewUrl = URL.createObjectURL(file);
        const result = await uploadBackgroundAssetV2(file, user._id, currentWorkspace._id, {
          organizationId: user.organization?._id,
          source: "s3",
        });
        setUploadedImages((prev) => [...prev, { previewUrl, publicUrl: result.url }]);
      }
      addNotification("Image attached as reference for video generation.", "info");
    } catch (error: any) {
      console.error("Error uploading image:", error);
      addNotification(error.response?.data?.message || "Failed to upload image", "error");
    } finally {
      setIsUploadingImage(false);
      // Reset input so same file can be selected again
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>, media: AnyMedia) => {
    event.stopPropagation();
    setMenuAnchor(event.currentTarget);
    setMenuMedia(media);
  };

  const handleMenuClose = () => {
    setMenuAnchor(null);
    setMenuMedia(null);
  };

  const handleDownload = (media?: AnyMedia | null) => {
    const target = media || menuMedia;
    const videoUrl = target ? getVideoUrl(target) : undefined;
    if (!videoUrl) return;
    const isVeo3 = target && isVeo3Media(target);
    const isSeedance = target && isSeedanceMedia(target);
    const isNanoBanana = target && isNanoBananaMedia(target);
    const isGemini = target && isGeminiMedia(target);
    const isWan3 = target && isWan3Media(target);
    const isHetzner = target && isHetznerMedia(target);
    const link = document.createElement("a");
    link.href = videoUrl;
    if (isNanoBanana) {
      const ext = (target as NanoBananaMedia).outputFormat || "jpg";
      link.download = `nano-banana-${target!.taskId}.${ext}`;
    } else {
      link.download = `${isHetzner ? "hetzner" : isVeo3 ? "veo3" : isWan3 ? "wan3" : isGemini ? "gemini" : isSeedance ? "seedance" : "sora2"}-${target!.taskId}.mp4`;
    }
    link.target = "_blank";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    handleMenuClose();
  };

  const handleDelete = async (media?: AnyMedia | null) => {
    const target = media || menuMedia;
    if (!target || !currentWorkspace) return;
    try {
      if (isHetznerMedia(target)) {
        await removeHetznerMediaFromWorkspace(currentWorkspace._id, target._id);
        setHetznerMedia((prev) => prev.filter((m) => m._id !== target._id));
      } else if (isWan3Media(target)) {
        await removeWan3MediaFromWorkspace(currentWorkspace._id, target._id);
        setWan3Media((prev) => prev.filter((m) => m._id !== target._id));
      } else if (isGeminiMedia(target)) {
        await removeGeminiMediaFromWorkspace(currentWorkspace._id, target._id);
        setGeminiMedia((prev) => prev.filter((m) => m._id !== target._id));
      } else if (isNanoBananaMedia(target)) {
        await removeNanoBananaMediaFromWorkspace(currentWorkspace._id, target._id);
        setNanoBananaMedia((prev) => prev.filter((m) => m._id !== target._id));
      } else if (isVeo3Media(target)) {
        await removeVeo3MediaFromWorkspace(currentWorkspace._id, target._id);
        setVeo3Media((prev) => prev.filter((m) => m._id !== target._id));
      } else if (isSeedanceMedia(target)) {
        await removeSeedanceMediaFromWorkspace(currentWorkspace._id, target._id);
        setSeedanceMedia((prev) => prev.filter((m) => m._id !== target._id));
      } else {
        await removeSora2MediaFromWorkspace(currentWorkspace._id, target._id);
        setWorkspaceMedia((prev) => prev.filter((m) => m._id !== target._id));
      }
      if (selectedMedia?._id === target._id) {
        setSelectedMedia(null);
      }
      addNotification("Media removed", "success");
    } catch (error: any) {
      console.error("Error deleting media:", error);
      addNotification(error.response?.data?.message || "Failed to delete media", "error");
    }
    handleMenuClose();
  };

  const handleUseAsReference = (media?: AnyMedia | null) => {
    const target = media || menuMedia;
    const videoUrl = target ? getVideoUrl(target) : undefined;
    if (!videoUrl) return;
    setUploadedImages((prev) => [
      ...prev,
      {
        previewUrl: videoUrl,
        publicUrl: videoUrl,
      },
    ]);
    addNotification("Media added as reference for new generation.", "info");
    setSelectedMedia(null);
    handleMenuClose();
  };

  const handleUseLastFrameAsReference = async (media?: AnyMedia | null) => {
    const target = media || menuMedia;
    const videoUrl = target ? getVideoUrl(target) : undefined;
    if (!videoUrl || !currentWorkspace || !user) return;
    handleMenuClose();
    setSelectedMedia(null);
    addNotification("Extracting last frame...", "info");

    try {
      const result = await extractLastFrame({
        videoUrl,
        workspaceId: currentWorkspace._id,
        userId: user._id,
        organizationId: user.organization?._id,
      });
      setUploadedImages((prev) => [...prev, { previewUrl: result.url, publicUrl: result.url }]);
      addNotification("Last frame added as reference for new generation.", "info");
    } catch (error: any) {
      console.error("Error extracting last frame:", error);
      addNotification(error.response?.data?.message || "Failed to extract last frame.", "error");
    }
  };

  const handleSaveToAssets = async (media?: AnyMedia | null) => {
    const target = media || menuMedia;
    if (!target || !currentWorkspace) return;
    setIsSavingToAssets(true);
    try {
      const saveData = {
        taskId: target.taskId,
        workspaceId: currentWorkspace._id,
        userId: user?._id,
        organizationId: user?.organization?._id,
      };
      let result;
      if (isHetznerMedia(target)) {
        result = await saveHetznerToAssets(saveData);
      } else if (isWan3Media(target)) {
        result = await saveWan3ToAssets(saveData);
      } else if (isGeminiMedia(target)) {
        result = await saveGeminiToAssets(saveData);
      } else if (isNanoBananaMedia(target)) {
        result = await saveNanoBananaToAssets(saveData);
      } else if (isVeo3Media(target)) {
        result = await saveVeo3ToAssets(saveData);
      } else if (isSeedanceMedia(target)) {
        result = await saveSeedanceToAssets(saveData);
      } else {
        result = await saveSora2ToAssets(saveData);
      }
      const assetUrl = result.data?.[0]?.url || "";
      const updateMedia = <T extends AnyMedia>(list: T[]) =>
        list.map((m) => (m._id === target._id ? { ...m, assetUrl } : m));
      if (isHetznerMedia(target)) {
        setHetznerMedia((prev) => updateMedia(prev));
      } else if (isWan3Media(target)) {
        setWan3Media((prev) => updateMedia(prev));
      } else if (isGeminiMedia(target)) {
        setGeminiMedia((prev) => updateMedia(prev));
      } else if (isNanoBananaMedia(target)) {
        setNanoBananaMedia((prev) => updateMedia(prev));
      } else if (isVeo3Media(target)) {
        setVeo3Media((prev) => updateMedia(prev));
      } else if (isSeedanceMedia(target)) {
        setSeedanceMedia((prev) => updateMedia(prev));
      } else {
        setWorkspaceMedia((prev) => updateMedia(prev));
      }
      if (selectedMedia?._id === target._id) {
        setSelectedMedia({ ...target, assetUrl });
      }
      addNotification("Saved to asset library", "success");
    } catch (error: any) {
      console.error("Error saving to assets:", error);
      addNotification(error.response?.data?.message || "Failed to save to assets", "error");
    } finally {
      setIsSavingToAssets(false);
    }
    handleMenuClose();
  };

  const handleRemoveWatermark = async (media?: AnyMedia | null) => {
    const target = media || menuMedia;
    if (!target || !currentWorkspace || isSeedanceMedia(target) || isGeminiMedia(target) || isWan3Media(target)) return;
    try {
      await removeSora2Watermark({
        taskId: target.taskId,
        workspaceId: currentWorkspace._id,
      });
      addNotification("Watermark removal started. This may take a moment.", "info");
      // Mark as processing locally so UI updates
      const updatedMedia: Sora2Media = {
        ...(target as Sora2Media),
        watermarkRemovalStatus: "processing" as const,
      };
      setWorkspaceMedia((prev) => prev.map((m) => (m._id === target._id ? updatedMedia : m)));
      if (selectedMedia?._id === target._id) {
        setSelectedMedia(updatedMedia);
      }
    } catch (error: any) {
      console.error("Error removing watermark:", error);
      addNotification(error.response?.data?.message || "Failed to remove watermark", "error");
    }
    handleMenuClose();
  };

  // Veo3: Extend video
  const handleExtend = async () => {
    if (!extendSourceMedia || !currentWorkspace || !extendPrompt.trim()) return;
    setIsExtending(true);
    try {
      const response = await createVeo3Extend({
        taskId: extendSourceMedia.taskId,
        prompt: extendPrompt.trim(),
        model: extendModel,
        seeds: veo3Seeds,
        watermark: veo3Watermark || undefined,
        workspaceId: currentWorkspace._id,
        userId: user?._id,
      });

      if (response.success) {
        addNotification("Veo3 extend started!", "info");
        const newMedia = response.data.veo3Media;
        setVeo3Media((prev) => [newMedia, ...prev]);
        setExtendDialogOpen(false);
        setExtendPrompt("");
        setSelectedMedia(null);

        const result = await pollForVeo3Result(response.data.taskId);
        if (result) {
          setVeo3Media((prev) => prev.map((m) => (m._id === newMedia._id ? result : m)));
          addNotification("Veo3 extended video generated!", "success");
        } else {
          setVeo3Media((prev) =>
            prev.map((m) => (m._id === newMedia._id ? { ...m, state: "fail" as const } : m))
          );
        }
      }
    } catch (error: any) {
      console.error("Error extending veo3 video:", error);
      addNotification(error.response?.data?.message || "Failed to extend video", "error");
    } finally {
      setIsExtending(false);
    }
  };

  // Prompt suggestion (claude-worker, preview-only — never triggers a real generation)
  const handleGetPromptSuggestion = async () => {
    if (!promptSuggestionInstructions.trim()) return;
    setIsGeneratingSuggestion(true);
    try {
      const response = await createPromptSuggestion({
        userInstructions: promptSuggestionInstructions.trim(),
        workspaceId: currentWorkspace?._id,
      });

      if (response.success) {
        const { jobId } = response.data;
        const MAX_SUGGESTION_POLL_ATTEMPTS = 40; // ~2 minutes at 3s intervals
        let result = null;
        for (let attempts = 0; attempts < MAX_SUGGESTION_POLL_ATTEMPTS; attempts++) {
          await new Promise((resolve) => setTimeout(resolve, 3000));
          const pollResponse = await getPromptSuggestionResult(jobId);
          if (pollResponse.data.status === "completed") {
            result = pollResponse.data;
            break;
          }
          if (pollResponse.data.status === "failed") {
            throw new Error(pollResponse.data.error || "Prompt suggestion failed");
          }
        }

        if (result && result.response) {
          setPrompt(result.response);
          setPromptSuggestionDialogOpen(false);
          setPromptSuggestionInstructions("");
          addNotification("Prompt suggestion added!", "success");
        } else {
          addNotification("Prompt suggestion timed out. Please try again.", "warning");
        }
      }
    } catch (error: any) {
      console.error("Error getting prompt suggestion:", error);
      addNotification(
        error.response?.data?.message || error.message || "Failed to get prompt suggestion",
        "error"
      );
    } finally {
      setIsGeneratingSuggestion(false);
    }
  };

  // Veo3: Fetch 1080p version
  const handleFetch1080p = async (media: Veo3Media) => {
    setIsFetching1080p(true);
    try {
      const response = await getVeo31080p(media.taskId);
      if (response.success) {
        const updated: Veo3Media = {
          ...media,
          videoUrl1080p: response.data.resultUrl,
        };
        setVeo3Media((prev) => prev.map((m) => (m._id === media._id ? updated : m)));
        if (selectedMedia?._id === media._id) {
          setSelectedMedia(updated);
        }
        addNotification("1080p version ready!", "success");
      }
    } catch (error: any) {
      console.error("Error fetching 1080p:", error);
      addNotification(error.response?.data?.message || "Failed to fetch 1080p version", "error");
    } finally {
      setIsFetching1080p(false);
    }
  };

  // Veo3: Start 4K upscaling
  const handleStart4K = async (media: Veo3Media) => {
    setIsStarting4K(true);
    try {
      const response = await startVeo3FourK({ taskId: media.taskId, index: 0 });
      if (response.success) {
        if (response.data.resultUrls) {
          // 4K already ready
          const updated: Veo3Media = {
            ...media,
            fourKState: "success",
            videoUrl4k: response.data.resultUrls[0],
          };
          setVeo3Media((prev) => prev.map((m) => (m._id === media._id ? updated : m)));
          if (selectedMedia?._id === media._id) {
            setSelectedMedia(updated);
          }
          addNotification("4K version ready!", "success");
        } else {
          // 4K processing
          const updated: Veo3Media = {
            ...media,
            fourKState: "processing",
          };
          setVeo3Media((prev) => prev.map((m) => (m._id === media._id ? updated : m)));
          if (selectedMedia?._id === media._id) {
            setSelectedMedia(updated);
          }
          addNotification("4K upscaling started. This may take a moment.", "info");
        }
      }
    } catch (error: any) {
      console.error("Error starting 4K:", error);
      addNotification(error.response?.data?.message || "Failed to start 4K upscaling", "error");
    } finally {
      setIsStarting4K(false);
    }
  };

  const isWorkspaceFree = currentWorkspace ? isFreeUser(currentWorkspace) : true;

  const showRemoveWatermarkButton = (media: AnyMedia | null | undefined) => {
    if (!media || media.state !== "success") return false;
    if (isSeedanceMedia(media) || isVeo3Media(media) || isNanoBananaMedia(media) || isGeminiMedia(media) || isWan3Media(media) || isHetznerMedia(media)) return false;
    // Free users see it disabled, premium/fullaccess see it only if not already removed
    if (isWorkspaceFree) return true;
    return media.watermarkRemoved === false || media.watermarkRemoved === undefined;
  };

  const truncatePrompt = (text: string, maxLen: number = 30) => {
    if (text.length <= maxLen) return text;
    return text.slice(0, maxLen) + "...";
  };

  return (
    <div className={classes.container}>
      {/* Carousel of past generations */}
      <div className={classes.carouselArea}>
        <div className={classes.carouselHeader}>
          <Typography
            variant="subtitle2"
            sx={{ fontWeight: 600, fontFamily: "Lexend, sans-serif" }}
          >
            Your Generations
          </Typography>
        </div>
        <Box sx={{ display: "flex", alignItems: "center", gap: "6px", mb: 1 }}>
          <InfoIcon sx={{ fontSize: 13, color: THEME_COLORS.darkYellow }} />
          <Typography sx={{ fontSize: "11px", color: THEME_COLORS.darkYellow, fontFamily: "Lexend, sans-serif" }}>
            Generated video URLs expire after a couple of days. Save any videos you want to keep to your library.
          </Typography>
        </Box>
        <div className={classes.carouselScroll}>
          {(() => {
            const isVeo3Mode = modelMode === "veo3";
            const isNanoBananaMode = modelMode === "nanobanana";
            const isGeminiMode = modelMode === "gemini";
            const isWan3Mode = modelMode === "wan3" || modelMode === "wan3prime";
            const isHetznerMode = modelMode === "hetzner";
            const isSeedanceMode =
              modelMode === "seedance" ||
              modelMode === "seedance15pro" ||
              modelMode === "seedance2" ||
              modelMode === "seedance2fast" ||
              modelMode === "seedance25";
            const loading = isHetznerMode
              ? isLoadingHetznerMedia
              : isWan3Mode
                ? isLoadingWan3Media
                : isGeminiMode
                  ? isLoadingGeminiMedia
                  : isNanoBananaMode
                    ? isLoadingNanoBananaMedia
                    : isVeo3Mode
                      ? isLoadingVeo3Media
                      : isSeedanceMode
                        ? isLoadingSeedanceMedia
                        : isLoadingMedia;
            const mediaList: AnyMedia[] = isHetznerMode
              ? hetznerMedia
              : isWan3Mode
                ? wan3Media
                : isGeminiMode
                  ? geminiMedia
                  : isNanoBananaMode
                    ? nanoBananaMedia
                    : isVeo3Mode
                      ? veo3Media
                      : isSeedanceMode
                        ? seedanceMedia
                        : workspaceMedia;
            const emptyMessage = isHetznerMode
              ? "No Local LLM Engine generations yet"
              : isWan3Mode
                ? "No Wan 3.0 generations yet"
                : isGeminiMode
                  ? "No Gemini generations yet"
                  : isNanoBananaMode
                    ? "No Nano Banana generations yet"
                    : isVeo3Mode
                      ? "No Veo3 generations yet"
                      : isSeedanceMode
                        ? "No Seedance generations yet"
                        : "No generations yet";

            if (loading) {
              return (
                <Box sx={{ display: "flex", alignItems: "center", gap: 1, py: 2 }}>
                  <CircularProgress size={20} sx={{ color: THEME_COLORS.yellow }} />
                  <Typography variant="caption" sx={{ color: "#888" }}>
                    Loading...
                  </Typography>
                </Box>
              );
            }
            if (mediaList.length === 0) {
              return (
                <div className={classes.mediaCardEmpty}>
                  <Box sx={{ textAlign: "center" }}>
                    <SmartDisplayIcon sx={{ fontSize: 32, color: "#ccc" }} />
                    <Typography variant="caption" display="block" sx={{ color: "#aaa", mt: 0.5 }}>
                      {emptyMessage}
                    </Typography>
                  </Box>
                </div>
              );
            }
            return mediaList.map((media) => (
              <div
                key={media._id}
                className={classes.mediaCard}
                onClick={() => setSelectedMedia(media)}
              >
                <span className={classes.mediaCardBadge}>
                  {isHetznerMedia(media)
                    ? "Local LLM Engine"
                    : isWan3Media(media)
                    ? (media as Wan3Media).model === "wan/3-0-video-prime"
                      ? "Wan 3.0 Prime"
                      : "Wan 3.0"
                    : isGeminiMedia(media)
                    ? "Gemini"
                    : isNanoBananaMedia(media)
                    ? "Nano Banana"
                    : isVeo3Media(media)
                    ? (media as Veo3Media).generationType === "EXTEND"
                      ? "Veo3 Extend"
                      : (media as Veo3Media).model === "veo3"
                        ? "Veo3 Quality"
                        : "Veo3 Fast"
                    : isSeedanceMedia(media)
                      ? (media as SeedanceMedia).generationType === "2.5"
                        ? "Seedance 2.5"
                        : (media as SeedanceMedia).generationType === "2.0-fast"
                          ? "Seedance 2.0 Fast"
                          : (media as SeedanceMedia).generationType === "2.0"
                            ? "Seedance 2.0"
                            : (media as SeedanceMedia).generationType === "1.5-pro"
                              ? "Seedance 1.5 Pro"
                              : "Seedance"
                      : (media as Sora2Media).generationType === "storyboard"
                        ? "Storyboard"
                        : (media as Sora2Media).generationType === "pro-text-to-video" ||
                            (media as Sora2Media).generationType === "pro-image-to-video"
                          ? "Sora2 Pro"
                          : "Sora2"}
                </span>
                {isMediaSuccess(media) && getVideoUrl(media) ? (
                  isImageMedia(media) ? (
                    <img
                      src={getVideoUrl(media)}
                      alt={media.prompt}
                      style={{ width: "100%", height: "100%", objectFit: "contain" }}
                    />
                  ) : (
                    <video src={getVideoUrl(media)} muted preload="metadata" />
                  )
                ) : isMediaFail(media) ? (
                  <Box className={classes.mediaCardOverlay}>
                    <SmartDisplayIcon sx={{ fontSize: 28, color: "#e57373" }} />
                    <Typography variant="caption" sx={{ color: "#e57373", fontSize: "10px" }}>
                      Failed
                    </Typography>
                  </Box>
                ) : isMediaSuccess(media) && !getVideoUrl(media) ? (
                  <Box className={classes.mediaCardOverlay}>
                    <SmartDisplayIcon sx={{ fontSize: 28, color: "#ff9800" }} />
                    <Typography variant="caption" sx={{ color: "#ff9800", fontSize: "10px" }}>
                      URL unavailable
                    </Typography>
                  </Box>
                ) : (
                  <Box className={classes.mediaCardOverlay}>
                    <CircularProgress size={24} sx={{ color: THEME_COLORS.yellow }} />
                    <Typography variant="caption" sx={{ color: "#888", fontSize: "10px" }}>
                      Generating...
                    </Typography>
                  </Box>
                )}
                <div className={classes.mediaCardInfo}>
                  <span className={classes.mediaCardPrompt}>{truncatePrompt(media.prompt)}</span>
                  <IconButton
                    size="small"
                    onClick={(e) => handleMenuOpen(e, media)}
                    sx={{ ml: 0.5, p: 0.3 }}
                  >
                    <MoreVertIcon sx={{ fontSize: 16 }} />
                  </IconButton>
                </div>
              </div>
            ));
          })()}
        </div>
      </div>

      {/* Current generation (above prompt) */}
      {currentGenMedia && (
        <div className={classes.currentGeneration}>
          {isMediaSuccess(currentGenMedia) && getVideoUrl(currentGenMedia) ? (
            isImageMedia(currentGenMedia) ? (
              <img
                src={getVideoUrl(currentGenMedia)}
                alt={currentGenMedia.prompt}
                style={{ width: "100%", objectFit: "contain" }}
              />
            ) : (
              <video controls src={getVideoUrl(currentGenMedia)} />
            )
          ) : (
            <div className={classes.currentGenLoading}>
              {isMediaSuccess(currentGenMedia) && !getVideoUrl(currentGenMedia) ? (
                <>
                  <SmartDisplayIcon sx={{ fontSize: 28, color: "#ff9800" }} />
                  <Typography variant="body2" sx={{ color: "#ff9800" }}>
                    {isImageMedia(currentGenMedia)
                      ? "Image generated but URL unavailable"
                      : "Video generated but URL unavailable"}
                  </Typography>
                </>
              ) : (
                <>
                  <CircularProgress size={24} sx={{ color: THEME_COLORS.yellow }} />
                  <Typography variant="body2" sx={{ color: "#888" }}>
                    {isMediaFail(currentGenMedia)
                      ? "Generation failed"
                      : modelMode === "nanobanana"
                        ? "Generating your image..."
                        : "Generating your video..."}
                  </Typography>
                </>
              )}
            </div>
          )}
        </div>
      )}

      {/* Empty state when nothing is being generated */}
      {!currentGenMedia && (
        <div className={classes.emptyState}>
          <div className={classes.emptyIcon}>
            <SmartDisplayIcon sx={{ fontSize: 36, color: "#ccc" }} />
          </div>
          <Typography
            variant="body1"
            sx={{
              color: "#999",
              textAlign: "center",
              maxWidth: "400px",
              fontSize: "14px",
              mb: 3,
            }}
          >
            Generate winning assets
          </Typography>
        </div>
      )}

      {/* Get a prompt suggestion from claude-worker (preview only) — above the
          prompt box, aligned left, so it's visible before the user starts typing */}
      <Box sx={{ display: "flex", justifyContent: "flex-start", mb: 1 }}>
        <Tooltip title="Get a suggestion from a trained Claude instance with winning ads (under test)">
          <Button
            size="small"
            onClick={() => setPromptSuggestionDialogOpen(true)}
            startIcon={<AutoAwesomeIcon sx={{ fontSize: 16 }} />}
            sx={{
              textTransform: "none",
              fontFamily: "Lexend, sans-serif",
              fontSize: "12px",
              fontWeight: 600,
              color: THEME_COLORS.darkYellow,
              backgroundColor: "rgba(0,0,0,0.04)",
              borderRadius: "8px",
              px: 1.2,
              minWidth: "auto",
              whiteSpace: "nowrap",
              "&:hover": { backgroundColor: "rgba(0,0,0,0.08)" },
            }}
          >
            Prompt Suggestion
          </Button>
        </Tooltip>
      </Box>

      {/* Prompt Input Area */}
      <div className={classes.promptContainer}>
        {modelMode === "storyboard" ? (
          /* Storyboard Shots UI */
          <div className={classes.shotsContainer}>
            {shots.map((shot, index) => (
              <div key={index} className={classes.shotRow}>
                <span className={classes.shotNumber}>Shot {index + 1}</span>
                <textarea
                  className={classes.shotInput}
                  placeholder={`Describe scene ${index + 1}...`}
                  value={shot.Scene}
                  onChange={(e) => updateShot(index, "Scene", e.target.value)}
                  rows={2}
                />
                <div className={classes.shotDuration}>
                  <input
                    className={classes.shotDurationInput}
                    type="number"
                    min={1}
                    max={25}
                    step={0.5}
                    value={shot.duration}
                    onChange={(e) => updateShot(index, "duration", parseFloat(e.target.value) || 1)}
                  />
                  <Typography variant="caption" sx={{ color: "#999", fontSize: "11px" }}>
                    sec
                  </Typography>
                </div>
                {shots.length > 1 && (
                  <IconButton
                    size="small"
                    onClick={() => removeShot(index)}
                    sx={{ p: 0.3, mt: 0.3 }}
                  >
                    <DeleteIcon sx={{ fontSize: 16, color: "#ccc" }} />
                  </IconButton>
                )}
              </div>
            ))}
            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
              <button className={classes.addShotButton} onClick={addShot} style={{ flex: 1 }}>
                <AddIcon sx={{ fontSize: 16 }} />
                Add scene
              </button>
              <Typography
                variant="caption"
                sx={{
                  ml: 1.5,
                  fontFamily: "Lexend, sans-serif",
                  fontSize: "11px",
                  color: totalShotDuration > maxDuration ? "#d32f2f" : "#999",
                  whiteSpace: "nowrap",
                }}
              >
                {totalShotDuration}s / {maxDuration}s
              </Typography>
            </Box>
          </div>
        ) : (
          /* Standard prompt textarea */
          <textarea
            className={classes.promptInput}
            placeholder={
              uploadedImages.length > 0
                ? "Describe how the image should animate..."
                : "Describe the video you want to generate..."
            }
            value={prompt}
            onChange={(e) => {
              const limit =
                modelMode === "seedance15pro"
                  ? SEEDANCE_15PRO_MAX_CHARS
                  : modelMode === "seedance2" || modelMode === "seedance2fast" || modelMode === "seedance25"
                    ? SEEDANCE_2_MAX_CHARS
                    : modelMode === "nanobanana"
                      ? NANO_BANANA_MAX_CHARS
                      : MAX_CHARS;
              if (e.target.value.length <= limit) {
                setPrompt(e.target.value);
              }
            }}
            onKeyDown={(e) => {
              if (e.key === "Enter" && !e.shiftKey && !isGenerating) {
                e.preventDefault();
                handleSubmit();
              }
            }}
          />
        )}

        {/* Uploaded images preview */}
        {uploadedImages.length > 0 && (
          <Box sx={{ display: "flex", gap: 1, px: 2, pb: 1, flexWrap: "wrap" }}>
            {uploadedImages.map((img, i) => (
              <Box
                key={i}
                sx={{
                  position: "relative",
                  width: 60,
                  height: 60,
                  borderRadius: "8px",
                  overflow: "hidden",
                  border: "1px solid #eee",
                }}
              >
                <img
                  src={img.previewUrl}
                  alt={`upload-${i}`}
                  style={{
                    width: "100%",
                    height: "100%",
                    objectFit: "cover",
                  }}
                />
                <IconButton
                  size="small"
                  onClick={() => setUploadedImages((prev) => prev.filter((_, idx) => idx !== i))}
                  sx={{
                    position: "absolute",
                    top: -4,
                    right: -4,
                    backgroundColor: "rgba(0,0,0,0.6)",
                    color: "#fff",
                    width: 20,
                    height: 20,
                    "&:hover": { backgroundColor: "rgba(0,0,0,0.8)" },
                  }}
                >
                  <DeleteIcon sx={{ fontSize: 12 }} />
                </IconButton>
              </Box>
            ))}
          </Box>
        )}

        {/* Toolbar */}
        <div className={classes.promptToolbar}>
          <div className={classes.toolbarLeft}>
            {/* Model Chip - clickable */}
            <div className={classes.modelChip} onClick={(e) => setModelMenuAnchor(e.currentTarget)}>
              {modelMode === "storyboard" ? (
                <StoryboardIcon sx={{ fontSize: 16, color: THEME_COLORS.darkYellow }} />
              ) : modelMode === "nanobanana" ? (
                <ImageIcon sx={{ fontSize: 16, color: THEME_COLORS.darkYellow }} />
              ) : (
                <SmartDisplayIcon sx={{ fontSize: 16, color: THEME_COLORS.darkYellow }} />
              )}
              {modelMode === "nanobanana"
                ? "Nano Banana"
                : modelMode === "hetzner"
                ? "Local LLM Engine"
                : modelMode === "wan3prime"
                ? "Wan 3.0 Prime"
                : modelMode === "wan3"
                ? "Wan 3.0 Video"
                : modelMode === "gemini"
                ? "Gemini Omni 1.1 Flash"
                : modelMode === "veo3"
                ? `Veo3 ${veo3Model === "veo3" ? "Quality" : "Fast"}`
                : modelMode === "seedance25"
                  ? "Seedance 2.5"
                  : modelMode === "seedance2fast"
                    ? "Seedance 2.0 Fast"
                    : modelMode === "seedance2"
                      ? "Seedance 2.0"
                      : modelMode === "seedance15pro"
                        ? "Seedance 1.5 Pro"
                        : modelMode === "seedance"
                          ? "Seedance"
                          : modelMode === "storyboard"
                            ? "Sora2 Pro Storyboard"
                            : modelMode === "sora2pro"
                              ? "Sora2 Pro"
                              : "Sora2"}
              <ExpandMoreIcon sx={{ fontSize: 14, color: "#999", ml: -0.5 }} />
            </div>

            {/* Image attach for image-to-video */}
            <Tooltip title="Attach reference image">
              <IconButton
                size="small"
                onClick={() => fileInputRef.current?.click()}
                disabled={isUploadingImage}
              >
                {isUploadingImage ? (
                  <CircularProgress size={18} />
                ) : (
                  <AttachFileIcon sx={{ fontSize: 18 }} />
                )}
              </IconButton>
            </Tooltip>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              multiple
              hidden
              onChange={handleImageUpload}
            />
          </div>

          <div className={classes.toolbarRight}>
            {/* Character Count (only for standard mode) */}
            {modelMode !== "storyboard" && (
              <span className={classes.charCount}>
                {prompt.length} /{" "}
                {(modelMode === "seedance15pro"
                  ? SEEDANCE_15PRO_MAX_CHARS
                  : modelMode === "seedance2" || modelMode === "seedance2fast"
                    ? SEEDANCE_2_MAX_CHARS
                    : modelMode === "nanobanana"
                      ? NANO_BANANA_MAX_CHARS
                      : MAX_CHARS
                ).toLocaleString()}
              </span>
            )}

            {/* Quantity Control (only for standard mode) */}
            {modelMode !== "storyboard" && (
              <div className={classes.quantityControl}>
                <IconButton
                  size="small"
                  onClick={() => setQuantity(Math.max(1, quantity - 1))}
                  disabled={quantity <= 1}
                  sx={{ p: 0.3 }}
                >
                  <Typography sx={{ fontSize: "16px", fontWeight: "bold", lineHeight: 1 }}>
                    -
                  </Typography>
                </IconButton>
                <input className={classes.quantityInput} type="text" value={quantity} readOnly />
                <IconButton
                  size="small"
                  onClick={() => setQuantity(Math.min(3, quantity + 1))}
                  disabled={quantity >= 3}
                  sx={{ p: 0.3 }}
                >
                  <Typography sx={{ fontSize: "16px", fontWeight: "bold", lineHeight: 1 }}>
                    +
                  </Typography>
                </IconButton>
              </div>
            )}

            {/* Settings - engine-specific */}
            {modelMode === "nanobanana" ? (
              <>
                {/* Nano Banana Aspect Ratio */}
                <Select
                  size="small"
                  value={nanoBananaAspectRatio}
                  onChange={(e) =>
                    setNanoBananaAspectRatio(e.target.value as NanoBananaAspectRatio)
                  }
                  sx={{
                    fontFamily: "Lexend, sans-serif",
                    fontSize: "12px",
                    height: "30px",
                    "& .MuiSelect-select": { py: 0.5, px: 1 },
                  }}
                >
                  <MenuItem value="auto">Auto</MenuItem>
                  <MenuItem value="1:1">1:1</MenuItem>
                  <MenuItem value="2:3">2:3</MenuItem>
                  <MenuItem value="3:2">3:2</MenuItem>
                  <MenuItem value="3:4">3:4</MenuItem>
                  <MenuItem value="4:3">4:3</MenuItem>
                  <MenuItem value="4:5">4:5</MenuItem>
                  <MenuItem value="5:4">5:4</MenuItem>
                  <MenuItem value="9:16">9:16</MenuItem>
                  <MenuItem value="16:9">16:9</MenuItem>
                  <MenuItem value="21:9">21:9</MenuItem>
                  <MenuItem value="1:4">1:4</MenuItem>
                  <MenuItem value="4:1">4:1</MenuItem>
                  <MenuItem value="1:8">1:8</MenuItem>
                  <MenuItem value="8:1">8:1</MenuItem>
                </Select>
                {/* Nano Banana Resolution */}
                <Select
                  size="small"
                  value={nanoBananaResolution}
                  onChange={(e) =>
                    setNanoBananaResolution(e.target.value as NanoBananaResolution)
                  }
                  sx={{
                    fontFamily: "Lexend, sans-serif",
                    fontSize: "12px",
                    height: "30px",
                    "& .MuiSelect-select": { py: 0.5, px: 1 },
                  }}
                >
                  <MenuItem value="1K">1K</MenuItem>
                  <MenuItem value="2K">2K</MenuItem>
                  <MenuItem value="4K">4K</MenuItem>
                </Select>
                {/* Nano Banana Output Format */}
                <Select
                  size="small"
                  value={nanoBananaOutputFormat}
                  onChange={(e) =>
                    setNanoBananaOutputFormat(e.target.value as NanoBananaOutputFormat)
                  }
                  sx={{
                    fontFamily: "Lexend, sans-serif",
                    fontSize: "12px",
                    height: "30px",
                    "& .MuiSelect-select": { py: 0.5, px: 1 },
                  }}
                >
                  <MenuItem value="jpg">JPG</MenuItem>
                  <MenuItem value="png">PNG</MenuItem>
                </Select>
              </>
            ) : modelMode === "gemini" ? (
              <>
                {/* Gemini Aspect Ratio (2 options — simpler than Seedance) */}
                <Select
                  size="small"
                  value={geminiAspectRatio}
                  onChange={(e) => setGeminiAspectRatio(e.target.value as GeminiAspectRatio)}
                  sx={{
                    fontFamily: "Lexend, sans-serif",
                    fontSize: "12px",
                    height: "30px",
                    "& .MuiSelect-select": { py: 0.5, px: 1 },
                  }}
                >
                  <MenuItem value="16:9">16:9</MenuItem>
                  <MenuItem value="9:16">9:16</MenuItem>
                </Select>
                {/* Gemini Resolution */}
                <Select
                  size="small"
                  value={geminiResolution}
                  onChange={(e) => setGeminiResolution(e.target.value as GeminiResolution)}
                  sx={{
                    fontFamily: "Lexend, sans-serif",
                    fontSize: "12px",
                    height: "30px",
                    "& .MuiSelect-select": { py: 0.5, px: 1 },
                  }}
                >
                  <MenuItem value="360p">360p</MenuItem>
                  <MenuItem value="720p">720p</MenuItem>
                  <MenuItem value="1080p">1080p</MenuItem>
                  <MenuItem value="4k">4K</MenuItem>
                </Select>
                {/* Gemini Duration */}
                <Select
                  size="small"
                  value={geminiDuration}
                  onChange={(e) => setGeminiDuration(e.target.value as GeminiDuration)}
                  sx={{
                    fontFamily: "Lexend, sans-serif",
                    fontSize: "12px",
                    height: "30px",
                    "& .MuiSelect-select": { py: 0.5, px: 1 },
                  }}
                >
                  <MenuItem value="4">4s</MenuItem>
                  <MenuItem value="6">6s</MenuItem>
                  <MenuItem value="8">8s</MenuItem>
                  <MenuItem value="10">10s</MenuItem>
                </Select>
                {/* Gemini Seed (optional) */}
                <Tooltip title="Use the same seed value to reproduce similar results across generations. Leave empty for random.">
                  <TextField
                    size="small"
                    type="number"
                    placeholder="Seed"
                    value={geminiSeed ?? ""}
                    onChange={(e) => {
                      const val = parseInt(e.target.value, 10);
                      setGeminiSeed(isNaN(val) ? undefined : val);
                    }}
                    InputLabelProps={{ shrink: true }}
                    sx={{
                      width: 90,
                      "& .MuiInputBase-root": { height: 30, fontSize: "12px" },
                    }}
                  />
                </Tooltip>
              </>
            ) : modelMode === "wan3" || modelMode === "wan3prime" ? (
              <>
                {/* Resolution — 3 toggle buttons (480P/720P/1080P) — shared by Wan3 and Wan3 Prime */}
                <Box sx={{ display: "flex", gap: 0.5 }}>
                  {(["480P", "720P", "1080P"] as Wan3Resolution[]).map((res) => (
                    <Button
                      key={res}
                      size="small"
                      variant={wan3Resolution === res ? "contained" : "outlined"}
                      onClick={() => setWan3Resolution(res)}
                      sx={{
                        fontFamily: "Lexend, sans-serif",
                        fontSize: "11px",
                        height: "30px",
                        minWidth: "auto",
                        px: 1,
                        textTransform: "none",
                        borderColor: "#ddd",
                        color: wan3Resolution === res ? "#fff" : "#666",
                        backgroundColor: wan3Resolution === res ? "#333" : "transparent",
                        "&:hover": {
                          backgroundColor: wan3Resolution === res ? "#444" : "#f5f5f5",
                          borderColor: "#bbb",
                        },
                      }}
                    >
                      {res}
                    </Button>
                  ))}
                </Box>
                {/* Aspect Ratio — 6 toggle buttons */}
                <Box sx={{ display: "flex", gap: 0.5, flexWrap: "wrap" }}>
                  {(["adaptive", "16:9", "4:3", "1:1", "3:4", "9:16"] as Wan3AspectRatio[]).map(
                    (ar) => (
                      <Button
                        key={ar}
                        size="small"
                        variant={wan3AspectRatio === ar ? "contained" : "outlined"}
                        onClick={() => setWan3AspectRatio(ar)}
                        sx={{
                          fontFamily: "Lexend, sans-serif",
                          fontSize: "11px",
                          height: "30px",
                          minWidth: "auto",
                          px: 1,
                          textTransform: "none",
                          borderColor: "#ddd",
                          color: wan3AspectRatio === ar ? "#fff" : "#666",
                          backgroundColor: wan3AspectRatio === ar ? "#333" : "transparent",
                          "&:hover": {
                            backgroundColor: wan3AspectRatio === ar ? "#444" : "#f5f5f5",
                            borderColor: "#bbb",
                          },
                        }}
                      >
                        {ar === "adaptive" ? "Adaptive" : ar}
                      </Button>
                    )
                  )}
                </Box>
                {/* Duration (-1 = smart duration auto mode, through 30s) */}
                <Tooltip title="Duration in seconds. -1 lets the model auto-select a smart duration.">
                  <Select
                    size="small"
                    value={String(wan3Duration)}
                    onChange={(e) => setWan3Duration(parseInt(e.target.value, 10))}
                    sx={{
                      fontFamily: "Lexend, sans-serif",
                      fontSize: "12px",
                      height: "30px",
                      "& .MuiSelect-select": { py: 0.5, px: 1 },
                    }}
                  >
                    <MenuItem value="-1">Auto (smart)</MenuItem>
                    {Array.from({ length: 31 }, (_, i) => i).map((d) => (
                      <MenuItem key={d} value={String(d)}>
                        {d}s
                      </MenuItem>
                    ))}
                  </Select>
                </Tooltip>
                {/* Advanced settings popover (audio / NSFW checker / seed) */}
                <Tooltip title="More settings">
                  <IconButton
                    size="small"
                    onClick={(e) => setWan3AdvancedAnchor(e.currentTarget)}
                    sx={{ p: 0.3 }}
                  >
                    <SettingsIcon
                      sx={{
                        fontSize: 18,
                        color: wan3AdvancedAnchor ? THEME_COLORS.darkYellow : "#999",
                      }}
                    />
                  </IconButton>
                </Tooltip>
                <Menu
                  anchorEl={wan3AdvancedAnchor}
                  open={Boolean(wan3AdvancedAnchor)}
                  onClose={() => setWan3AdvancedAnchor(null)}
                  anchorOrigin={{ vertical: "top", horizontal: "right" }}
                  transformOrigin={{ vertical: "bottom", horizontal: "right" }}
                  MenuListProps={{ sx: { py: 0.5, minWidth: 240 } }}
                >
                  <MenuItem onClick={() => setWan3Audio(!wan3Audio)} sx={{ py: 0.5 }}>
                    <FormControlLabel
                      control={
                        <Switch
                          size="small"
                          checked={wan3Audio}
                          onChange={(e) => setWan3Audio(e.target.checked)}
                        />
                      }
                      label="Generate audio"
                      onClick={(e) => e.stopPropagation()}
                      sx={{
                        m: 0,
                        "& .MuiFormControlLabel-label": {
                          fontSize: "13px",
                          fontFamily: "Lexend, sans-serif",
                        },
                      }}
                    />
                  </MenuItem>
                  <MenuItem onClick={() => setWan3NsfwChecker(!wan3NsfwChecker)} sx={{ py: 0.5 }}>
                    <FormControlLabel
                      control={
                        <Switch
                          size="small"
                          checked={wan3NsfwChecker}
                          onChange={(e) => setWan3NsfwChecker(e.target.checked)}
                        />
                      }
                      label="NSFW checker"
                      onClick={(e) => e.stopPropagation()}
                      sx={{
                        m: 0,
                        "& .MuiFormControlLabel-label": {
                          fontSize: "13px",
                          fontFamily: "Lexend, sans-serif",
                        },
                      }}
                    />
                  </MenuItem>
                  <MenuItem
                    disableRipple
                    sx={{
                      py: 0.5,
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      "&:hover": { backgroundColor: "transparent" },
                    }}
                  >
                    <Typography
                      sx={{ fontSize: "13px", fontFamily: "Lexend, sans-serif", color: "#333" }}
                    >
                      Seed
                    </Typography>
                    <TextField
                      size="small"
                      type="number"
                      placeholder="Random"
                      value={wan3Seed ?? ""}
                      onClick={(e) => e.stopPropagation()}
                      onChange={(e) => {
                        const val = parseInt(e.target.value, 10);
                        setWan3Seed(isNaN(val) ? undefined : val);
                      }}
                      InputLabelProps={{ shrink: true }}
                      sx={{
                        width: 100,
                        "& .MuiInputBase-root": { height: 28, fontSize: "12px" },
                      }}
                    />
                  </MenuItem>
                </Menu>
              </>
            ) : modelMode === "veo3" ? (
              <>
                {/* Model Tier: Fast vs Quality */}
                <Select
                  size="small"
                  value={veo3Model}
                  onChange={(e) => setVeo3Model(e.target.value as Veo3Model)}
                  sx={{
                    fontFamily: "Lexend, sans-serif",
                    fontSize: "12px",
                    height: "30px",
                    "& .MuiSelect-select": { py: 0.5, px: 1 },
                  }}
                >
                  <MenuItem value="veo3_fast">Fast</MenuItem>
                  <MenuItem value="veo3">Quality</MenuItem>
                </Select>

                {/* Aspect Ratio */}
                <Select
                  size="small"
                  value={veo3AspectRatio}
                  onChange={(e) => setVeo3AspectRatio(e.target.value as Veo3AspectRatio)}
                  sx={{
                    fontFamily: "Lexend, sans-serif",
                    fontSize: "12px",
                    height: "30px",
                    "& .MuiSelect-select": { py: 0.5, px: 1 },
                  }}
                >
                  <MenuItem value="16:9">16:9</MenuItem>
                  <MenuItem value="9:16">9:16</MenuItem>
                  <MenuItem value="Auto">Auto</MenuItem>
                </Select>

                {/* Image Generation Type (only when images attached) */}
                {uploadedImages.length > 0 && (
                  <Select
                    size="small"
                    value={veo3ImageGenType}
                    onChange={(e) =>
                      setVeo3ImageGenType(
                        e.target.value as "FIRST_AND_LAST_FRAMES_2_VIDEO" | "REFERENCE_2_VIDEO"
                      )
                    }
                    sx={{
                      fontFamily: "Lexend, sans-serif",
                      fontSize: "12px",
                      height: "30px",
                      "& .MuiSelect-select": { py: 0.5, px: 1 },
                    }}
                  >
                    <MenuItem value="FIRST_AND_LAST_FRAMES_2_VIDEO">First/Last Frames</MenuItem>
                    <MenuItem value="REFERENCE_2_VIDEO">Reference Style</MenuItem>
                  </Select>
                )}

                {/* Advanced Settings Toggle */}
                <Tooltip title="Advanced settings">
                  <IconButton
                    size="small"
                    onClick={() => setShowVeo3Advanced(!showVeo3Advanced)}
                    sx={{ p: 0.3 }}
                  >
                    <SettingsIcon
                      sx={{
                        fontSize: 18,
                        color: showVeo3Advanced ? THEME_COLORS.darkYellow : "#999",
                      }}
                    />
                  </IconButton>
                </Tooltip>
              </>
            ) : modelMode === "seedance2fast" ? (
              <>
                {/* Aspect Ratio — always visible for Seedance 2.0 Fast (no 'adaptive') */}
                <Select
                  size="small"
                  value={seedance2FastAspectRatio}
                  onChange={(e) =>
                    setSeedance2FastAspectRatio(e.target.value as typeof seedance2FastAspectRatio)
                  }
                  sx={{
                    fontFamily: "Lexend, sans-serif",
                    fontSize: "12px",
                    height: "30px",
                    "& .MuiSelect-select": { py: 0.5, px: 1 },
                  }}
                >
                  <MenuItem value="16:9">16:9</MenuItem>
                  <MenuItem value="9:16">9:16</MenuItem>
                  <MenuItem value="1:1">1:1</MenuItem>
                  <MenuItem value="4:3">4:3</MenuItem>
                  <MenuItem value="3:4">3:4</MenuItem>
                  <MenuItem value="21:9">21:9</MenuItem>
                </Select>
                {/* Resolution (480p or 720p) */}
                <Select
                  size="small"
                  value={seedance2FastResolution}
                  onChange={(e) =>
                    setSeedance2FastResolution(e.target.value as "480p" | "720p")
                  }
                  sx={{
                    fontFamily: "Lexend, sans-serif",
                    fontSize: "12px",
                    height: "30px",
                    "& .MuiSelect-select": { py: 0.5, px: 1 },
                  }}
                >
                  <MenuItem value="480p">480p</MenuItem>
                  <MenuItem value="720p">720p</MenuItem>
                </Select>
                {/* Duration (integer 4-15) */}
                <Tooltip title="Duration in seconds (4-15)">
                  <Select
                    size="small"
                    value={String(seedance2FastDuration)}
                    onChange={(e) => setSeedance2FastDuration(parseInt(e.target.value, 10))}
                    sx={{
                      fontFamily: "Lexend, sans-serif",
                      fontSize: "12px",
                      height: "30px",
                      "& .MuiSelect-select": { py: 0.5, px: 1 },
                    }}
                  >
                    {Array.from({ length: 12 }, (_, i) => i + 4).map((d) => (
                      <MenuItem key={d} value={String(d)}>
                        {d}s
                      </MenuItem>
                    ))}
                  </Select>
                </Tooltip>
                {/* Advanced settings popover (audio / web search / NSFW) */}
                <Tooltip title="More settings">
                  <IconButton
                    size="small"
                    onClick={(e) => setSeedance2FastAdvancedAnchor(e.currentTarget)}
                    sx={{ p: 0.3 }}
                  >
                    <SettingsIcon
                      sx={{
                        fontSize: 18,
                        color: seedance2FastAdvancedAnchor ? THEME_COLORS.darkYellow : "#999",
                      }}
                    />
                  </IconButton>
                </Tooltip>
                <Menu
                  anchorEl={seedance2FastAdvancedAnchor}
                  open={Boolean(seedance2FastAdvancedAnchor)}
                  onClose={() => setSeedance2FastAdvancedAnchor(null)}
                  anchorOrigin={{ vertical: "top", horizontal: "right" }}
                  transformOrigin={{ vertical: "bottom", horizontal: "right" }}
                  MenuListProps={{ sx: { py: 0.5, minWidth: 220 } }}
                >
                  <MenuItem
                    onClick={() => setSeedance2FastGenerateAudio(!seedance2FastGenerateAudio)}
                    sx={{ py: 0.5 }}
                  >
                    <FormControlLabel
                      control={
                        <Switch
                          size="small"
                          checked={seedance2FastGenerateAudio}
                          onChange={(e) => setSeedance2FastGenerateAudio(e.target.checked)}
                        />
                      }
                      label="Generate audio"
                      onClick={(e) => e.stopPropagation()}
                      sx={{
                        m: 0,
                        "& .MuiFormControlLabel-label": {
                          fontSize: "13px",
                          fontFamily: "Lexend, sans-serif",
                        },
                      }}
                    />
                  </MenuItem>
                  <MenuItem
                    onClick={() => setSeedance2FastWebSearch(!seedance2FastWebSearch)}
                    sx={{ py: 0.5 }}
                  >
                    <FormControlLabel
                      control={
                        <Switch
                          size="small"
                          checked={seedance2FastWebSearch}
                          onChange={(e) => setSeedance2FastWebSearch(e.target.checked)}
                        />
                      }
                      label="Web search"
                      onClick={(e) => e.stopPropagation()}
                      sx={{
                        m: 0,
                        "& .MuiFormControlLabel-label": {
                          fontSize: "13px",
                          fontFamily: "Lexend, sans-serif",
                        },
                      }}
                    />
                  </MenuItem>
                  <MenuItem
                    onClick={() => setSeedance2FastNsfwChecker(!seedance2FastNsfwChecker)}
                    sx={{ py: 0.5 }}
                  >
                    <FormControlLabel
                      control={
                        <Switch
                          size="small"
                          checked={seedance2FastNsfwChecker}
                          onChange={(e) => setSeedance2FastNsfwChecker(e.target.checked)}
                        />
                      }
                      label="NSFW checker"
                      onClick={(e) => e.stopPropagation()}
                      sx={{
                        m: 0,
                        "& .MuiFormControlLabel-label": {
                          fontSize: "13px",
                          fontFamily: "Lexend, sans-serif",
                        },
                      }}
                    />
                  </MenuItem>
                </Menu>
              </>
            ) : modelMode === "seedance2" ? (
              <>
                {/* Aspect Ratio — always visible for Seedance 2.0 */}
                <Select
                  size="small"
                  value={seedance2AspectRatio}
                  onChange={(e) =>
                    setSeedance2AspectRatio(e.target.value as typeof seedance2AspectRatio)
                  }
                  sx={{
                    fontFamily: "Lexend, sans-serif",
                    fontSize: "12px",
                    height: "30px",
                    "& .MuiSelect-select": { py: 0.5, px: 1 },
                  }}
                >
                  <MenuItem value="16:9">16:9</MenuItem>
                  <MenuItem value="9:16">9:16</MenuItem>
                  <MenuItem value="1:1">1:1</MenuItem>
                  <MenuItem value="4:3">4:3</MenuItem>
                  <MenuItem value="3:4">3:4</MenuItem>
                  <MenuItem value="21:9">21:9</MenuItem>
                  <MenuItem value="adaptive">Adaptive</MenuItem>
                </Select>
                {/* Resolution (480p or 720p in 2.0) */}
                <Select
                  size="small"
                  value={seedance2Resolution}
                  onChange={(e) =>
                    setSeedance2Resolution(e.target.value as "480p" | "720p")
                  }
                  sx={{
                    fontFamily: "Lexend, sans-serif",
                    fontSize: "12px",
                    height: "30px",
                    "& .MuiSelect-select": { py: 0.5, px: 1 },
                  }}
                >
                  <MenuItem value="480p">480p</MenuItem>
                  <MenuItem value="720p">720p</MenuItem>
                </Select>
                {/* Duration (integer 4-15) */}
                <Tooltip title="Duration in seconds (4-15)">
                  <Select
                    size="small"
                    value={String(seedance2Duration)}
                    onChange={(e) => setSeedance2Duration(parseInt(e.target.value, 10))}
                    sx={{
                      fontFamily: "Lexend, sans-serif",
                      fontSize: "12px",
                      height: "30px",
                      "& .MuiSelect-select": { py: 0.5, px: 1 },
                    }}
                  >
                    {Array.from({ length: 12 }, (_, i) => i + 4).map((d) => (
                      <MenuItem key={d} value={String(d)}>
                        {d}s
                      </MenuItem>
                    ))}
                  </Select>
                </Tooltip>
                {/* Advanced settings popover (audio / web search / NSFW) */}
                <Tooltip title="More settings">
                  <IconButton
                    size="small"
                    onClick={(e) => setSeedance2AdvancedAnchor(e.currentTarget)}
                    sx={{ p: 0.3 }}
                  >
                    <SettingsIcon
                      sx={{
                        fontSize: 18,
                        color: seedance2AdvancedAnchor ? THEME_COLORS.darkYellow : "#999",
                      }}
                    />
                  </IconButton>
                </Tooltip>
                <Menu
                  anchorEl={seedance2AdvancedAnchor}
                  open={Boolean(seedance2AdvancedAnchor)}
                  onClose={() => setSeedance2AdvancedAnchor(null)}
                  anchorOrigin={{ vertical: "top", horizontal: "right" }}
                  transformOrigin={{ vertical: "bottom", horizontal: "right" }}
                  MenuListProps={{ sx: { py: 0.5, minWidth: 220 } }}
                >
                  <MenuItem
                    onClick={() => setSeedance2GenerateAudio(!seedance2GenerateAudio)}
                    sx={{ py: 0.5 }}
                  >
                    <FormControlLabel
                      control={
                        <Switch
                          size="small"
                          checked={seedance2GenerateAudio}
                          onChange={(e) => setSeedance2GenerateAudio(e.target.checked)}
                        />
                      }
                      label="Generate audio"
                      onClick={(e) => e.stopPropagation()}
                      sx={{
                        m: 0,
                        "& .MuiFormControlLabel-label": {
                          fontSize: "13px",
                          fontFamily: "Lexend, sans-serif",
                        },
                      }}
                    />
                  </MenuItem>
                  <MenuItem
                    onClick={() => setSeedance2WebSearch(!seedance2WebSearch)}
                    sx={{ py: 0.5 }}
                  >
                    <FormControlLabel
                      control={
                        <Switch
                          size="small"
                          checked={seedance2WebSearch}
                          onChange={(e) => setSeedance2WebSearch(e.target.checked)}
                        />
                      }
                      label="Web search"
                      onClick={(e) => e.stopPropagation()}
                      sx={{
                        m: 0,
                        "& .MuiFormControlLabel-label": {
                          fontSize: "13px",
                          fontFamily: "Lexend, sans-serif",
                        },
                      }}
                    />
                  </MenuItem>
                  <MenuItem
                    onClick={() => setSeedance2NsfwChecker(!seedance2NsfwChecker)}
                    sx={{ py: 0.5 }}
                  >
                    <FormControlLabel
                      control={
                        <Switch
                          size="small"
                          checked={seedance2NsfwChecker}
                          onChange={(e) => setSeedance2NsfwChecker(e.target.checked)}
                        />
                      }
                      label="NSFW checker"
                      onClick={(e) => e.stopPropagation()}
                      sx={{
                        m: 0,
                        "& .MuiFormControlLabel-label": {
                          fontSize: "13px",
                          fontFamily: "Lexend, sans-serif",
                        },
                      }}
                    />
                  </MenuItem>
                </Menu>
              </>
            ) : modelMode === "seedance25" ? (
              <>
                {/* Aspect Ratio — same set as Seedance 2.0, includes 'adaptive'.
                    Kie rejects Seedance 2.5 first-frame / first-last-frame tasks unless
                    aspect_ratio is 'adaptive', so lock the picker to that whenever 1-2
                    reference images are attached (3+ goes through multimodal reference
                    mode instead, which isn't restricted). */}
                <Tooltip
                  title={
                    uploadedImages.length === 1 || uploadedImages.length === 2
                      ? "Locked to Adaptive: Seedance 2.5 requires adaptive aspect ratio when using a first frame (or first+last frame) reference image. Attach 3+ images for multimodal reference mode instead if you need a fixed ratio."
                      : ""
                  }
                >
                  <span>
                    <Select
                      size="small"
                      disabled={uploadedImages.length === 1 || uploadedImages.length === 2}
                      value={
                        uploadedImages.length === 1 || uploadedImages.length === 2
                          ? "adaptive"
                          : seedance25AspectRatio
                      }
                      onChange={(e) =>
                        setSeedance25AspectRatio(e.target.value as typeof seedance25AspectRatio)
                      }
                      sx={{
                        fontFamily: "Lexend, sans-serif",
                        fontSize: "12px",
                        height: "30px",
                        "& .MuiSelect-select": { py: 0.5, px: 1 },
                      }}
                    >
                      <MenuItem value="16:9">16:9</MenuItem>
                      <MenuItem value="9:16">9:16</MenuItem>
                      <MenuItem value="1:1">1:1</MenuItem>
                      <MenuItem value="4:3">4:3</MenuItem>
                      <MenuItem value="3:4">3:4</MenuItem>
                      <MenuItem value="21:9">21:9</MenuItem>
                      <MenuItem value="adaptive">Adaptive</MenuItem>
                    </Select>
                  </span>
                </Tooltip>
                {/* Resolution (480p/720p/1080p — 2.5 adds 1080p over 2.0) */}
                <Select
                  size="small"
                  value={seedance25Resolution}
                  onChange={(e) =>
                    setSeedance25Resolution(e.target.value as "480p" | "720p" | "1080p")
                  }
                  sx={{
                    fontFamily: "Lexend, sans-serif",
                    fontSize: "12px",
                    height: "30px",
                    "& .MuiSelect-select": { py: 0.5, px: 1 },
                  }}
                >
                  <MenuItem value="480p">480p</MenuItem>
                  <MenuItem value="720p">720p</MenuItem>
                  <MenuItem value="1080p">1080p</MenuItem>
                </Select>
                {/* Duration (integer 4-30 — native cap is higher than 2.0's 15) */}
                <Tooltip title="Duration in seconds (4-30)">
                  <Select
                    size="small"
                    value={String(seedance25Duration)}
                    onChange={(e) => setSeedance25Duration(parseInt(e.target.value, 10))}
                    sx={{
                      fontFamily: "Lexend, sans-serif",
                      fontSize: "12px",
                      height: "30px",
                      "& .MuiSelect-select": { py: 0.5, px: 1 },
                    }}
                  >
                    {Array.from({ length: 27 }, (_, i) => i + 4).map((d) => (
                      <MenuItem key={d} value={String(d)}>
                        {d}s
                      </MenuItem>
                    ))}
                  </Select>
                </Tooltip>
                {/* Advanced settings popover (audio / return last frame / output format / web search / NSFW) */}
                <Tooltip title="More settings">
                  <IconButton
                    size="small"
                    onClick={(e) => setSeedance25AdvancedAnchor(e.currentTarget)}
                    sx={{ p: 0.3 }}
                  >
                    <SettingsIcon
                      sx={{
                        fontSize: 18,
                        color: seedance25AdvancedAnchor ? THEME_COLORS.darkYellow : "#999",
                      }}
                    />
                  </IconButton>
                </Tooltip>
                <Menu
                  anchorEl={seedance25AdvancedAnchor}
                  open={Boolean(seedance25AdvancedAnchor)}
                  onClose={() => setSeedance25AdvancedAnchor(null)}
                  anchorOrigin={{ vertical: "top", horizontal: "right" }}
                  transformOrigin={{ vertical: "bottom", horizontal: "right" }}
                  MenuListProps={{ sx: { py: 0.5, minWidth: 240 } }}
                >
                  <MenuItem
                    onClick={() => setSeedance25GenerateAudio(!seedance25GenerateAudio)}
                    sx={{ py: 0.5 }}
                  >
                    <FormControlLabel
                      control={
                        <Switch
                          size="small"
                          checked={seedance25GenerateAudio}
                          onChange={(e) => setSeedance25GenerateAudio(e.target.checked)}
                        />
                      }
                      label="Generate audio"
                      onClick={(e) => e.stopPropagation()}
                      sx={{
                        m: 0,
                        "& .MuiFormControlLabel-label": {
                          fontSize: "13px",
                          fontFamily: "Lexend, sans-serif",
                        },
                      }}
                    />
                  </MenuItem>
                  <MenuItem
                    onClick={() => setSeedance25ReturnLastFrame(!seedance25ReturnLastFrame)}
                    sx={{ py: 0.5 }}
                  >
                    <FormControlLabel
                      control={
                        <Switch
                          size="small"
                          checked={seedance25ReturnLastFrame}
                          onChange={(e) => setSeedance25ReturnLastFrame(e.target.checked)}
                        />
                      }
                      label="Return last frame"
                      onClick={(e) => e.stopPropagation()}
                      sx={{
                        m: 0,
                        "& .MuiFormControlLabel-label": {
                          fontSize: "13px",
                          fontFamily: "Lexend, sans-serif",
                        },
                      }}
                    />
                  </MenuItem>
                  <MenuItem
                    onClick={() => setSeedance25WebSearch(!seedance25WebSearch)}
                    sx={{ py: 0.5 }}
                  >
                    <FormControlLabel
                      control={
                        <Switch
                          size="small"
                          checked={seedance25WebSearch}
                          onChange={(e) => setSeedance25WebSearch(e.target.checked)}
                        />
                      }
                      label="Web search"
                      onClick={(e) => e.stopPropagation()}
                      sx={{
                        m: 0,
                        "& .MuiFormControlLabel-label": {
                          fontSize: "13px",
                          fontFamily: "Lexend, sans-serif",
                        },
                      }}
                    />
                  </MenuItem>
                  <MenuItem
                    onClick={() => setSeedance25NsfwChecker(!seedance25NsfwChecker)}
                    sx={{ py: 0.5 }}
                  >
                    <FormControlLabel
                      control={
                        <Switch
                          size="small"
                          checked={seedance25NsfwChecker}
                          onChange={(e) => setSeedance25NsfwChecker(e.target.checked)}
                        />
                      }
                      label="NSFW checker"
                      onClick={(e) => e.stopPropagation()}
                      sx={{
                        m: 0,
                        "& .MuiFormControlLabel-label": {
                          fontSize: "13px",
                          fontFamily: "Lexend, sans-serif",
                        },
                      }}
                    />
                  </MenuItem>
                  <MenuItem
                    disableRipple
                    sx={{
                      py: 0.5,
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      "&:hover": { backgroundColor: "transparent" },
                    }}
                  >
                    <Typography
                      sx={{ fontSize: "13px", fontFamily: "Lexend, sans-serif", color: "#333" }}
                    >
                      Output format
                    </Typography>
                    <Box sx={{ display: "flex", gap: 0.5 }} onClick={(e) => e.stopPropagation()}>
                      <Button
                        size="small"
                        variant={seedance25OutputFormat === "mp4" ? "contained" : "outlined"}
                        onClick={() => setSeedance25OutputFormat("mp4")}
                        sx={{
                          fontFamily: "Lexend, sans-serif",
                          fontSize: "11px",
                          minWidth: "auto",
                          px: 1,
                          textTransform: "none",
                          borderColor: "#ddd",
                          color: seedance25OutputFormat === "mp4" ? "#fff" : "#666",
                          backgroundColor: seedance25OutputFormat === "mp4" ? "#333" : "transparent",
                          "&:hover": {
                            backgroundColor: seedance25OutputFormat === "mp4" ? "#444" : "#f5f5f5",
                            borderColor: "#bbb",
                          },
                        }}
                      >
                        MP4
                      </Button>
                      <Button
                        size="small"
                        variant={seedance25OutputFormat === "mov" ? "contained" : "outlined"}
                        onClick={() => setSeedance25OutputFormat("mov")}
                        sx={{
                          fontFamily: "Lexend, sans-serif",
                          fontSize: "11px",
                          minWidth: "auto",
                          px: 1,
                          textTransform: "none",
                          borderColor: "#ddd",
                          color: seedance25OutputFormat === "mov" ? "#fff" : "#666",
                          backgroundColor: seedance25OutputFormat === "mov" ? "#333" : "transparent",
                          "&:hover": {
                            backgroundColor: seedance25OutputFormat === "mov" ? "#444" : "#f5f5f5",
                            borderColor: "#bbb",
                          },
                        }}
                      >
                        MOV
                      </Button>
                    </Box>
                  </MenuItem>
                </Menu>
              </>
            ) : modelMode === "seedance15pro" ? (
              <>
                {/* Aspect Ratio (text-to-video only — image mode follows the image's ratio) */}
                {uploadedImages.length === 0 && (
                  <Select
                    size="small"
                    value={seedanceAspectRatio}
                    onChange={(e) => setSeedanceAspectRatio(e.target.value as SeedanceAspectRatio)}
                    sx={{
                      fontFamily: "Lexend, sans-serif",
                      fontSize: "12px",
                      height: "30px",
                      "& .MuiSelect-select": { py: 0.5, px: 1 },
                    }}
                  >
                    <MenuItem value="16:9">16:9</MenuItem>
                    <MenuItem value="9:16">9:16</MenuItem>
                    <MenuItem value="1:1">1:1</MenuItem>
                    <MenuItem value="4:3">4:3</MenuItem>
                    <MenuItem value="3:4">3:4</MenuItem>
                    <MenuItem value="21:9">21:9</MenuItem>
                  </Select>
                )}
                {/* Resolution */}
                <Select
                  size="small"
                  value={seedanceResolution}
                  onChange={(e) => setSeedanceResolution(e.target.value as SeedanceResolution)}
                  sx={{
                    fontFamily: "Lexend, sans-serif",
                    fontSize: "12px",
                    height: "30px",
                    "& .MuiSelect-select": { py: 0.5, px: 1 },
                  }}
                >
                  <MenuItem value="480p">480p</MenuItem>
                  <MenuItem value="720p">720p</MenuItem>
                  <MenuItem value="1080p">1080p</MenuItem>
                </Select>
                {/* Duration (4/8/12s) */}
                <Select
                  size="small"
                  value={seedance15Duration}
                  onChange={(e) => setSeedance15Duration(e.target.value as "4" | "8" | "12")}
                  sx={{
                    fontFamily: "Lexend, sans-serif",
                    fontSize: "12px",
                    height: "30px",
                    "& .MuiSelect-select": { py: 0.5, px: 1 },
                  }}
                >
                  <MenuItem value="4">4s</MenuItem>
                  <MenuItem value="8">8s</MenuItem>
                  <MenuItem value="12">12s</MenuItem>
                </Select>
                {/* Fixed Lens toggle */}
                <Tooltip title="Keep camera fixed (no motion)">
                  <Button
                    size="small"
                    variant={fixedLens ? "contained" : "outlined"}
                    onClick={() => setFixedLens(!fixedLens)}
                    sx={{
                      fontFamily: "Lexend, sans-serif",
                      fontSize: "11px",
                      height: "30px",
                      minWidth: "auto",
                      px: 1,
                      textTransform: "none",
                      borderColor: "#ddd",
                      color: fixedLens ? "#fff" : "#666",
                      backgroundColor: fixedLens ? "#333" : "transparent",
                      "&:hover": {
                        backgroundColor: fixedLens ? "#444" : "#f5f5f5",
                        borderColor: "#bbb",
                      },
                    }}
                  >
                    Fixed Lens
                  </Button>
                </Tooltip>
                {/* Generate Audio toggle */}
                <Tooltip title="Generate audio with the video">
                  <Button
                    size="small"
                    variant={generateAudio ? "contained" : "outlined"}
                    onClick={() => setGenerateAudio(!generateAudio)}
                    sx={{
                      fontFamily: "Lexend, sans-serif",
                      fontSize: "11px",
                      height: "30px",
                      minWidth: "auto",
                      px: 1,
                      textTransform: "none",
                      borderColor: "#ddd",
                      color: generateAudio ? "#fff" : "#666",
                      backgroundColor: generateAudio ? "#333" : "transparent",
                      "&:hover": {
                        backgroundColor: generateAudio ? "#444" : "#f5f5f5",
                        borderColor: "#bbb",
                      },
                    }}
                  >
                    Audio
                  </Button>
                </Tooltip>
              </>
            ) : modelMode === "seedance" ? (
              <>
                {/* Seedance Aspect Ratio (text-to-video only — image mode follows the image's ratio) */}
                {uploadedImages.length === 0 && (
                  <Select
                    size="small"
                    value={seedanceAspectRatio}
                    onChange={(e) => setSeedanceAspectRatio(e.target.value as SeedanceAspectRatio)}
                    sx={{
                      fontFamily: "Lexend, sans-serif",
                      fontSize: "12px",
                      height: "30px",
                      "& .MuiSelect-select": { py: 0.5, px: 1 },
                    }}
                  >
                    <MenuItem value="16:9">16:9</MenuItem>
                    <MenuItem value="9:16">9:16</MenuItem>
                    <MenuItem value="1:1">1:1</MenuItem>
                    <MenuItem value="4:3">4:3</MenuItem>
                    <MenuItem value="3:4">3:4</MenuItem>
                    <MenuItem value="21:9">21:9</MenuItem>
                  </Select>
                )}
                {/* Seedance Resolution */}
                <Select
                  size="small"
                  value={seedanceResolution}
                  onChange={(e) => setSeedanceResolution(e.target.value as SeedanceResolution)}
                  sx={{
                    fontFamily: "Lexend, sans-serif",
                    fontSize: "12px",
                    height: "30px",
                    "& .MuiSelect-select": { py: 0.5, px: 1 },
                  }}
                >
                  <MenuItem value="480p">480p</MenuItem>
                  <MenuItem value="720p">720p</MenuItem>
                  <MenuItem value="1080p">1080p</MenuItem>
                </Select>
                {/* Seedance Duration */}
                <Select
                  size="small"
                  value={seedanceDuration}
                  onChange={(e) => setSeedanceDuration(e.target.value as SeedanceDuration)}
                  sx={{
                    fontFamily: "Lexend, sans-serif",
                    fontSize: "12px",
                    height: "30px",
                    "& .MuiSelect-select": { py: 0.5, px: 1 },
                  }}
                >
                  <MenuItem value="5">5s</MenuItem>
                  <MenuItem value="10">10s</MenuItem>
                </Select>
              </>
            ) : modelMode === "hetzner" ? (
              <>
                {/* Resolution — landscape (fast turbo path) vs portrait (slower, non-turbo) */}
                <Box sx={{ display: "flex", gap: 0.5 }}>
                  {(["1344x768", "768x1344"] as const).map((res) => (
                    <Button
                      key={res}
                      size="small"
                      variant={hetznerResolution === res ? "contained" : "outlined"}
                      onClick={() => setHetznerResolution(res)}
                      sx={{
                        fontFamily: "Lexend, sans-serif",
                        fontSize: "11px",
                        height: "30px",
                        minWidth: "auto",
                        px: 1,
                        textTransform: "none",
                        borderColor: "#ddd",
                        color: hetznerResolution === res ? "#fff" : "#666",
                        backgroundColor: hetznerResolution === res ? "#333" : "transparent",
                        "&:hover": {
                          backgroundColor: hetznerResolution === res ? "#444" : "#f5f5f5",
                          borderColor: "#bbb",
                        },
                      }}
                    >
                      {res === "1344x768" ? "Landscape" : "Portrait"}
                    </Button>
                  ))}
                </Box>
                {/* Duration (gateway max is 15s) */}
                <Tooltip title="Duration in seconds (max 15s on this server).">
                  <Select
                    size="small"
                    value={String(hetznerDurationS)}
                    onChange={(e) => setHetznerDurationS(parseInt(e.target.value, 10))}
                    sx={{
                      fontFamily: "Lexend, sans-serif",
                      fontSize: "12px",
                      height: "30px",
                      "& .MuiSelect-select": { py: 0.5, px: 1 },
                    }}
                  >
                    {Array.from({ length: 15 }, (_, i) => i + 1).map((d) => (
                      <MenuItem key={d} value={String(d)}>
                        {d}s
                      </MenuItem>
                    ))}
                  </Select>
                </Tooltip>
              </>
            ) : (
              <>
                {/* Sora2 Aspect Ratio */}
                <Select
                  size="small"
                  value={aspectRatio}
                  onChange={(e) => setAspectRatio(e.target.value as Sora2AspectRatio)}
                  sx={{
                    fontFamily: "Lexend, sans-serif",
                    fontSize: "12px",
                    height: "30px",
                    "& .MuiSelect-select": { py: 0.5, px: 1 },
                  }}
                >
                  <MenuItem value="landscape">Landscape</MenuItem>
                  <MenuItem value="portrait">Portrait</MenuItem>
                </Select>
                {/* Sora2 Duration */}
                <Select
                  size="small"
                  value={nFrames}
                  onChange={(e) => setNFrames(e.target.value as Sora2Frames)}
                  sx={{
                    fontFamily: "Lexend, sans-serif",
                    fontSize: "12px",
                    height: "30px",
                    "& .MuiSelect-select": { py: 0.5, px: 1 },
                  }}
                >
                  <MenuItem value="10">10s</MenuItem>
                  <MenuItem value="15">15s</MenuItem>
                  {modelMode === "storyboard" && <MenuItem value="25">25s</MenuItem>}
                </Select>
                {/* Size (Pro only) */}
                {modelMode === "sora2pro" && (
                  <Select
                    size="small"
                    value={size}
                    onChange={(e) => setSize(e.target.value as Sora2Size)}
                    sx={{
                      fontFamily: "Lexend, sans-serif",
                      fontSize: "12px",
                      height: "30px",
                      "& .MuiSelect-select": { py: 0.5, px: 1 },
                    }}
                  >
                    <MenuItem value="high">High quality</MenuItem>
                    <MenuItem value="standard">Standard</MenuItem>
                  </Select>
                )}
              </>
            )}

            {/* Submit Button */}
            <Tooltip title={isGenerating ? "Generating..." : "Generate"}>
              <span>
                <button
                  className={classes.submitButton}
                  onClick={handleSubmit}
                  disabled={!canSubmit()}
                >
                  {isGenerating ? (
                    <CircularProgress size={18} sx={{ color: "#fff" }} />
                  ) : (
                    <ArrowUpwardIcon sx={{ fontSize: 20 }} />
                  )}
                </button>
              </span>
            </Tooltip>
          </div>
        </div>

        {/* Veo3 Advanced Settings (collapsible) */}
        {modelMode === "veo3" && showVeo3Advanced && (
          <Box
            sx={{
              px: 2,
              pb: 1.5,
              display: "flex",
              gap: 1.5,
              alignItems: "center",
              flexWrap: "wrap",
              borderTop: "1px solid #f0f0f0",
              pt: 1,
            }}
          >
            <Tooltip title="Use the same seed value to reproduce similar results across generations. Leave empty for random.">
              <TextField
                size="small"
                label="Seed"
                type="number"
                value={veo3Seeds ?? ""}
                onChange={(e) => {
                  const val = parseInt(e.target.value);
                  setVeo3Seeds(isNaN(val) ? undefined : Math.min(99999, Math.max(10000, val)));
                }}
                placeholder="10000-99999"
                inputProps={{ min: 10000, max: 99999 }}
                InputLabelProps={{ shrink: true }}
                sx={{
                  width: 120,
                  "& .MuiInputBase-root": { height: 32, fontSize: "12px" },
                  "& .MuiInputLabel-root": { color: "#555" },
                }}
              />
            </Tooltip>
            <FormControlLabel
              control={
                <Switch
                  size="small"
                  checked={veo3EnableTranslation}
                  onChange={(e) => setVeo3EnableTranslation(e.target.checked)}
                />
              }
              label={
                <Typography
                  sx={{ fontSize: "12px", fontFamily: "Lexend, sans-serif", color: "#555" }}
                >
                  Translate prompt
                </Typography>
              }
            />
            <Tooltip title="Add custom text to be embedded as a watermark on the generated video. Leave empty for no watermark.">
              <TextField
                size="small"
                label="Watermark"
                value={veo3Watermark}
                onChange={(e) => setVeo3Watermark(e.target.value)}
                placeholder="Optional watermark text"
                InputLabelProps={{ shrink: true }}
                sx={{
                  width: 160,
                  "& .MuiInputBase-root": { height: 32, fontSize: "12px" },
                  "& .MuiInputLabel-root": { color: "#555" },
                }}
              />
            </Tooltip>
          </Box>
        )}
      </div>

      {/* Per-engine cost disclaimer, shown below the prompt input for every ModelMode */}
      <Box sx={{ display: "flex", alignItems: "center", gap: "6px", px: 2, pt: 0.5 }}>
        <InfoIcon sx={{ fontSize: 13, color: THEME_COLORS.darkYellow }} />
        <Typography sx={{ fontSize: "11px", color: THEME_COLORS.darkYellow, fontFamily: "Lexend, sans-serif" }}>
          {getCostDisclaimer(modelMode, veo3Model)}
        </Typography>
      </Box>

      {/* Model Selector Menu */}
      <Menu
        anchorEl={modelMenuAnchor}
        open={Boolean(modelMenuAnchor)}
        onClose={() => setModelMenuAnchor(null)}
        anchorOrigin={{ vertical: "top", horizontal: "left" }}
        transformOrigin={{ vertical: "bottom", horizontal: "left" }}
      >
        <MenuItem
          onClick={() => {
            setModelMode("sora2");
            setModelMenuAnchor(null);
          }}
          selected={modelMode === "sora2"}
        >
          <ListItemIcon>
            <SmartDisplayIcon fontSize="small" sx={{ color: THEME_COLORS.darkYellow }} />
          </ListItemIcon>
          <ListItemText
            primary="Sora2"
            secondary="Text or image to video"
            primaryTypographyProps={{ fontSize: "13px", fontWeight: 500 }}
            secondaryTypographyProps={{ fontSize: "11px", color: "#888" }}
          />
          <Chip
            label="unstable"
            size="small"
            sx={{
              ml: 1,
              height: 18,
              fontSize: "10px",
              fontWeight: 600,
              backgroundColor: "#ff9800",
              color: "#fff",
              "& .MuiChip-label": { px: 0.8 },
            }}
          />
        </MenuItem>
        <MenuItem
          onClick={() => {
            setModelMode("sora2pro");
            setModelMenuAnchor(null);
          }}
          selected={modelMode === "sora2pro"}
        >
          <ListItemIcon>
            <SmartDisplayIcon fontSize="small" sx={{ color: THEME_COLORS.darkYellow }} />
          </ListItemIcon>
          <ListItemText
            primary="Sora2 Pro"
            secondary="Higher quality text or image to video"
            primaryTypographyProps={{ fontSize: "13px", fontWeight: 500 }}
            secondaryTypographyProps={{ fontSize: "11px", color: "#888" }}
          />
          <Chip
            label="unstable"
            size="small"
            sx={{
              ml: 1,
              height: 18,
              fontSize: "10px",
              fontWeight: 600,
              backgroundColor: "#ff9800",
              color: "#fff",
              "& .MuiChip-label": { px: 0.8 },
            }}
          />
        </MenuItem>
        <MenuItem
          onClick={() => {
            setModelMode("storyboard");
            setModelMenuAnchor(null);
          }}
          selected={modelMode === "storyboard"}
        >
          <ListItemIcon>
            <StoryboardIcon fontSize="small" sx={{ color: THEME_COLORS.darkYellow }} />
          </ListItemIcon>
          <ListItemText
            primary="Sora2 Pro Storyboard"
            secondary="Multi-scene video with shot control"
            primaryTypographyProps={{ fontSize: "13px", fontWeight: 500 }}
            secondaryTypographyProps={{ fontSize: "11px", color: "#888" }}
          />
          <Chip
            label="unstable"
            size="small"
            sx={{
              ml: 1,
              height: 18,
              fontSize: "10px",
              fontWeight: 600,
              backgroundColor: "#ff9800",
              color: "#fff",
              "& .MuiChip-label": { px: 0.8 },
            }}
          />
        </MenuItem>
        <MenuItem
          onClick={() => {
            setModelMode("seedance");
            setModelMenuAnchor(null);
          }}
          selected={modelMode === "seedance"}
        >
          <ListItemIcon>
            <SmartDisplayIcon fontSize="small" sx={{ color: THEME_COLORS.darkYellow }} />
          </ListItemIcon>
          <ListItemText
            primary="Seedance"
            secondary="ByteDance Seedance V1 Pro"
            primaryTypographyProps={{ fontSize: "13px", fontWeight: 500 }}
            secondaryTypographyProps={{ fontSize: "11px", color: "#888" }}
          />
        </MenuItem>
        <MenuItem
          onClick={() => {
            setModelMode("seedance15pro");
            setModelMenuAnchor(null);
          }}
          selected={modelMode === "seedance15pro"}
        >
          <ListItemIcon>
            <SmartDisplayIcon fontSize="small" sx={{ color: THEME_COLORS.darkYellow }} />
          </ListItemIcon>
          <ListItemText
            primary="Seedance 1.5 Pro"
            secondary="Latest Seedance with audio generation"
            primaryTypographyProps={{ fontSize: "13px", fontWeight: 500 }}
            secondaryTypographyProps={{ fontSize: "11px", color: "#888" }}
          />
        </MenuItem>
        <MenuItem
          onClick={() => {
            setModelMode("seedance2");
            setModelMenuAnchor(null);
          }}
          selected={modelMode === "seedance2"}
        >
          <ListItemIcon>
            <SmartDisplayIcon fontSize="small" sx={{ color: THEME_COLORS.darkYellow }} />
          </ListItemIcon>
          <ListItemText
            primary="Seedance 2.0"
            secondary="Multimodal reference, first+last frame, adaptive aspect"
            primaryTypographyProps={{ fontSize: "13px", fontWeight: 500 }}
            secondaryTypographyProps={{ fontSize: "11px", color: "#888" }}
          />
          <Chip
            label="new"
            size="small"
            sx={{
              ml: 1,
              height: 18,
              fontSize: "10px",
              fontWeight: 600,
              backgroundColor: THEME_COLORS.yellow,
              color: "#000",
              "& .MuiChip-label": { px: 0.8 },
            }}
          />
        </MenuItem>
        <MenuItem
          onClick={() => {
            setModelMode("seedance2fast");
            setModelMenuAnchor(null);
          }}
          selected={modelMode === "seedance2fast"}
        >
          <ListItemIcon>
            <SmartDisplayIcon fontSize="small" sx={{ color: THEME_COLORS.darkYellow }} />
          </ListItemIcon>
          <ListItemText
            primary="Seedance 2.0 Fast"
            secondary="Faster Seedance 2.0 — multimodal references, no first/last frame"
            primaryTypographyProps={{ fontSize: "13px", fontWeight: 500 }}
            secondaryTypographyProps={{ fontSize: "11px", color: "#888" }}
          />
          <Chip
            label="new"
            size="small"
            sx={{
              ml: 1,
              height: 18,
              fontSize: "10px",
              fontWeight: 600,
              backgroundColor: THEME_COLORS.yellow,
              color: "#000",
              "& .MuiChip-label": { px: 0.8 },
            }}
          />
        </MenuItem>
        <MenuItem
          onClick={() => {
            setModelMode("seedance25");
            setModelMenuAnchor(null);
          }}
          selected={modelMode === "seedance25"}
        >
          <ListItemIcon>
            <SmartDisplayIcon fontSize="small" sx={{ color: THEME_COLORS.darkYellow }} />
          </ListItemIcon>
          <ListItemText
            primary="Seedance 2.5"
            secondary="Longer duration (up to 30s), MOV output, return last frame"
            primaryTypographyProps={{ fontSize: "13px", fontWeight: 500 }}
            secondaryTypographyProps={{ fontSize: "11px", color: "#888" }}
          />
          <Chip
            label="new"
            size="small"
            sx={{
              ml: 1,
              height: 18,
              fontSize: "10px",
              fontWeight: 600,
              backgroundColor: THEME_COLORS.yellow,
              color: "#000",
              "& .MuiChip-label": { px: 0.8 },
            }}
          />
        </MenuItem>
        <MenuItem
          onClick={() => {
            setModelMode("veo3");
            setModelMenuAnchor(null);
          }}
          selected={modelMode === "veo3"}
        >
          <ListItemIcon>
            <SmartDisplayIcon fontSize="small" sx={{ color: THEME_COLORS.darkYellow }} />
          </ListItemIcon>
          <ListItemText
            primary="Veo3"
            secondary="Google Veo3 with extend, 1080p, and 4K"
            primaryTypographyProps={{ fontSize: "13px", fontWeight: 500 }}
            secondaryTypographyProps={{ fontSize: "11px", color: "#888" }}
          />
        </MenuItem>
        <MenuItem
          onClick={() => {
            setModelMode("nanobanana");
            setModelMenuAnchor(null);
          }}
          selected={modelMode === "nanobanana"}
        >
          <ListItemIcon>
            <ImageIcon fontSize="small" sx={{ color: THEME_COLORS.darkYellow }} />
          </ListItemIcon>
          <ListItemText
            primary="Nano Banana"
            secondary="Text or image to image — Nano Banana 2"
            primaryTypographyProps={{ fontSize: "13px", fontWeight: 500 }}
            secondaryTypographyProps={{ fontSize: "11px", color: "#888" }}
          />
          <Chip
            label="new"
            size="small"
            sx={{
              ml: 1,
              height: 18,
              fontSize: "10px",
              fontWeight: 600,
              backgroundColor: THEME_COLORS.yellow,
              color: "#000",
              "& .MuiChip-label": { px: 0.8 },
            }}
          />
        </MenuItem>
        <MenuItem
          onClick={() => {
            setModelMode("gemini");
            setModelMenuAnchor(null);
          }}
          selected={modelMode === "gemini"}
        >
          <ListItemIcon>
            <SmartDisplayIcon fontSize="small" sx={{ color: THEME_COLORS.darkYellow }} />
          </ListItemIcon>
          <ListItemText
            primary="Gemini Omni 1.1 Flash"
            secondary="Google Gemini text or image to video"
            primaryTypographyProps={{ fontSize: "13px", fontWeight: 500 }}
            secondaryTypographyProps={{ fontSize: "11px", color: "#888" }}
          />
          <Chip
            label="new"
            size="small"
            sx={{
              ml: 1,
              height: 18,
              fontSize: "10px",
              fontWeight: 600,
              backgroundColor: THEME_COLORS.yellow,
              color: "#000",
              "& .MuiChip-label": { px: 0.8 },
            }}
          />
        </MenuItem>
        <MenuItem
          onClick={() => {
            setModelMode("wan3");
            setModelMenuAnchor(null);
          }}
          selected={modelMode === "wan3"}
        >
          <ListItemIcon>
            <SmartDisplayIcon fontSize="small" sx={{ color: THEME_COLORS.darkYellow }} />
          </ListItemIcon>
          <ListItemText
            primary="Wan 3.0 Video"
            secondary="Text or image to video with smart duration and reference frames"
            primaryTypographyProps={{ fontSize: "13px", fontWeight: 500 }}
            secondaryTypographyProps={{ fontSize: "11px", color: "#888" }}
          />
          <Chip
            label="new"
            size="small"
            sx={{
              ml: 1,
              height: 18,
              fontSize: "10px",
              fontWeight: 600,
              backgroundColor: THEME_COLORS.yellow,
              color: "#000",
              "& .MuiChip-label": { px: 0.8 },
            }}
          />
        </MenuItem>
        <MenuItem
          onClick={() => {
            setModelMode("wan3prime");
            setModelMenuAnchor(null);
          }}
          selected={modelMode === "wan3prime"}
        >
          <ListItemIcon>
            <SmartDisplayIcon fontSize="small" sx={{ color: THEME_COLORS.darkYellow }} />
          </ListItemIcon>
          <ListItemText
            primary="Wan 3.0 Prime"
            secondary="Higher-quality Wan 3.0 tier, same text/image to video controls"
            primaryTypographyProps={{ fontSize: "13px", fontWeight: 500 }}
            secondaryTypographyProps={{ fontSize: "11px", color: "#888" }}
          />
          <Chip
            label="new"
            size="small"
            sx={{
              ml: 1,
              height: 18,
              fontSize: "10px",
              fontWeight: 600,
              backgroundColor: THEME_COLORS.yellow,
              color: "#000",
              "& .MuiChip-label": { px: 0.8 },
            }}
          />
        </MenuItem>
        <MenuItem
          onClick={() => {
            setModelMode("hetzner");
            setModelMenuAnchor(null);
          }}
          selected={modelMode === "hetzner"}
        >
          <ListItemIcon>
            <SmartDisplayIcon fontSize="small" sx={{ color: THEME_COLORS.darkYellow }} />
          </ListItemIcon>
          <ListItemText
            primary="Local LLM Engine"
            secondary="Self-hosted MiniMax H3 on our own GPU server — text to video"
            primaryTypographyProps={{ fontSize: "13px", fontWeight: 500 }}
            secondaryTypographyProps={{ fontSize: "11px", color: "#888" }}
          />
          <Box sx={{ display: "flex", gap: 0.5, ml: 1 }}>
            <Chip
              label="new"
              size="small"
              sx={{
                height: 18,
                fontSize: "10px",
                fontWeight: 600,
                backgroundColor: THEME_COLORS.yellow,
                color: "#000",
                "& .MuiChip-label": { px: 0.8 },
              }}
            />
            <Chip
              label="free of charge"
              size="small"
              sx={{
                height: 18,
                fontSize: "10px",
                fontWeight: 600,
                backgroundColor: "#43e0a3",
                color: "#000",
                "& .MuiChip-label": { px: 0.8 },
              }}
            />
            <Chip
              label="under test"
              size="small"
              sx={{
                height: 18,
                fontSize: "10px",
                fontWeight: 600,
                backgroundColor: "#ffc24d",
                color: "#000",
                "& .MuiChip-label": { px: 0.8 },
              }}
            />
          </Box>
        </MenuItem>
      </Menu>

      {/* Three-dot Menu for media cards */}
      <Menu
        anchorEl={menuAnchor}
        open={Boolean(menuAnchor)}
        onClose={handleMenuClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <MenuItem onClick={() => handleDownload()} disabled={!menuMedia || !getVideoUrl(menuMedia)}>
          <ListItemIcon>
            <DownloadIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Download</ListItemText>
        </MenuItem>
        {menuMedia &&
          isMediaSuccess(menuMedia) &&
          getVideoUrl(menuMedia) &&
          !isImageMedia(menuMedia) && (
            <Tooltip
              title="Extract the last frame of this video and use it as a reference image for a new generation"
              placement="right"
            >
              <MenuItem onClick={() => handleUseLastFrameAsReference()}>
                <ListItemIcon>
                  <LastFrameIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText>Use Last Frame as Reference</ListItemText>
              </MenuItem>
            </Tooltip>
          )}
        {menuMedia && isMediaSuccess(menuMedia) && getVideoUrl(menuMedia) && (
          <Tooltip
            title="Add this media as a reference image for a new generation"
            placement="right"
          >
            <MenuItem onClick={() => handleUseAsReference()}>
              <ListItemIcon>
                <UseAsRefIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText>Use as Reference</ListItemText>
            </MenuItem>
          </Tooltip>
        )}
        {/* Veo3: Extend Video */}
        {menuMedia &&
          isVeo3Media(menuMedia) &&
          isMediaSuccess(menuMedia) &&
          !menuMedia.videoUrl1080p && (
            <MenuItem
              onClick={() => {
                setExtendSourceMedia(menuMedia as Veo3Media);
                setExtendDialogOpen(true);
                handleMenuClose();
              }}
            >
              <ListItemIcon>
                <ExtendIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText>Extend Video</ListItemText>
            </MenuItem>
          )}
        {/* Veo3: Get 1080p */}
        {menuMedia &&
          isVeo3Media(menuMedia) &&
          isMediaSuccess(menuMedia) &&
          !menuMedia.videoUrl1080p && (
            <MenuItem
              onClick={() => {
                handleFetch1080p(menuMedia as Veo3Media);
                handleMenuClose();
              }}
              disabled={isFetching1080p}
            >
              <ListItemIcon>
                <HdIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText>{isFetching1080p ? "Fetching 1080p..." : "Get 1080p"}</ListItemText>
            </MenuItem>
          )}
        {/* Veo3: 4K Upscale */}
        {menuMedia &&
          isVeo3Media(menuMedia) &&
          isMediaSuccess(menuMedia) &&
          !menuMedia.fourKTaskId && (
            <MenuItem
              onClick={() => {
                handleStart4K(menuMedia as Veo3Media);
                handleMenuClose();
              }}
              disabled={isStarting4K}
            >
              <ListItemIcon>
                <FourKIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText>{isStarting4K ? "Starting 4K..." : "Upscale to 4K"}</ListItemText>
            </MenuItem>
          )}
        {/* Veo3: 4K in progress */}
        {menuMedia &&
          isVeo3Media(menuMedia) &&
          (menuMedia.fourKState === "pending" || menuMedia.fourKState === "processing") && (
            <MenuItem disabled>
              <ListItemIcon>
                <CircularProgress size={16} />
              </ListItemIcon>
              <ListItemText>4K upscaling...</ListItemText>
            </MenuItem>
          )}
        {menuMedia && isMediaSuccess(menuMedia) && !menuMedia.assetUrl && (
          <Tooltip
            title="Save this video to your asset library so it remains accessible after the generation URL expires. You can also use it with templates or as a background."
            placement="right"
          >
            <MenuItem onClick={() => handleSaveToAssets()} disabled={isSavingToAssets}>
              <ListItemIcon>
                <SaveAltIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText>
                {isSavingToAssets ? "Saving..." : "Save to Asset Library"}
              </ListItemText>
            </MenuItem>
          </Tooltip>
        )}
        {menuMedia && menuMedia.assetUrl && (
          <MenuItem
            onClick={() => {
              window.open(menuMedia.assetUrl!, "_blank");
              handleMenuClose();
            }}
          >
            <ListItemIcon>
              <OpenInNewIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>Open Asset in New Tab</ListItemText>
          </MenuItem>
        )}
        {menuMedia &&
          !isSeedanceMedia(menuMedia) &&
          !isNanoBananaMedia(menuMedia) &&
          !isGeminiMedia(menuMedia) &&
          !isWan3Media(menuMedia) &&
          !isHetznerMedia(menuMedia) &&
          (menuMedia.generationType === "text-to-video" ||
            menuMedia.generationType === "image-to-video") &&
          showRemoveWatermarkButton(menuMedia) && (
            <MenuItem
              onClick={() => handleRemoveWatermark()}
              disabled={isWorkspaceFree || menuMedia?.watermarkRemovalStatus === "processing"}
            >
              <ListItemIcon>
                <WatermarkIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText>
                {menuMedia?.watermarkRemovalStatus === "processing"
                  ? "Removing watermark..."
                  : "Remove Watermark"}
              </ListItemText>
            </MenuItem>
          )}
        <MenuItem onClick={() => { setPendingDeleteMedia(menuMedia); handleMenuClose(); }}>
          <ListItemIcon>
            <DeleteIcon fontSize="small" sx={{ color: "#d32f2f" }} />
          </ListItemIcon>
          <ListItemText sx={{ color: "#d32f2f" }}>Delete</ListItemText>
        </MenuItem>
      </Menu>

      {/* Media Preview Modal */}
      <Dialog
        open={Boolean(selectedMedia)}
        onClose={() => setSelectedMedia(null)}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: "16px",
            overflow: "hidden",
            fontFamily: "Lexend, sans-serif",
          },
        }}
      >
        <DialogContent sx={{ p: 0 }}>
          {selectedMedia && (
            <>
              {/* Video / Loading / Error area */}
              <Box className={classes.modalMediaArea}>
                {isMediaSuccess(selectedMedia) && getVideoUrl(selectedMedia) ? (
                  isImageMedia(selectedMedia) ? (
                    <img
                      src={getVideoUrl(selectedMedia)}
                      alt={selectedMedia.prompt}
                      className={classes.modalVideo}
                      style={{ objectFit: "contain" }}
                    />
                  ) : (
                    <video
                      controls
                      autoPlay
                      src={getVideoUrl(selectedMedia)}
                      className={classes.modalVideo}
                    />
                  )
                ) : isMediaFail(selectedMedia) ? (
                  <Box className={classes.modalStatusArea}>
                    <SmartDisplayIcon sx={{ fontSize: 48, color: "#e57373" }} />
                    <Typography variant="body1" sx={{ color: "#e57373", mt: 1 }}>
                      Generation failed
                    </Typography>
                    {selectedMedia.failMsg && (
                      <Typography variant="caption" sx={{ color: "#999", mt: 0.5 }}>
                        {selectedMedia.failMsg}
                      </Typography>
                    )}
                  </Box>
                ) : isMediaSuccess(selectedMedia) && !getVideoUrl(selectedMedia) ? (
                  <Box className={classes.modalStatusArea}>
                    <SmartDisplayIcon sx={{ fontSize: 48, color: "#ff9800" }} />
                    <Typography variant="body1" sx={{ color: "#ff9800", mt: 1 }}>
                      {isImageMedia(selectedMedia)
                        ? "Image generated but URL unavailable"
                        : "Video generated but URL unavailable"}
                    </Typography>
                    <Typography variant="caption" sx={{ color: "#aaa", mt: 0.5 }}>
                      {isImageMedia(selectedMedia)
                        ? "The image was processed but the URL was not saved. Please check the backend logs."
                        : "The video was processed but the URL was not saved. Please check the backend logs."}
                    </Typography>
                  </Box>
                ) : (
                  <Box className={classes.modalStatusArea}>
                    <CircularProgress size={40} sx={{ color: THEME_COLORS.yellow }} />
                    <Typography variant="body1" sx={{ color: "#888", mt: 1.5 }}>
                      {isImageMedia(selectedMedia)
                        ? "Generating your image..."
                        : "Generating your video..."}
                    </Typography>
                    <Typography variant="caption" sx={{ color: "#aaa", mt: 0.5 }}>
                      This may take a few moments
                    </Typography>
                  </Box>
                )}
              </Box>

              {/* Prompt text */}
              <Box sx={{ px: 3, py: 1.5 }}>
                <Typography
                  variant="body2"
                  sx={{
                    color: "#666",
                    fontFamily: "Lexend, sans-serif",
                    fontSize: "13px",
                  }}
                >
                  {selectedMedia.prompt}
                </Typography>
              </Box>
            </>
          )}
        </DialogContent>
        <DialogActions
          sx={{
            px: 3,
            pb: 2,
            pt: 0,
            gap: 1,
            justifyContent: "flex-start",
          }}
        >
          <Button
            startIcon={<DownloadIcon />}
            onClick={() => handleDownload(selectedMedia)}
            disabled={selectedMedia?.state !== "success"}
            variant="outlined"
            size="small"
            sx={{
              textTransform: "none",
              fontFamily: "Lexend, sans-serif",
              borderRadius: "8px",
              borderColor: "#ddd",
              color: "#555",
              "&:hover": {
                borderColor: "#bbb",
                backgroundColor: "#fafafa",
              },
              "&.Mui-disabled": {
                borderColor: "#eee",
                color: "#ccc",
              },
            }}
          >
            Download
          </Button>
          {selectedMedia &&
            isMediaSuccess(selectedMedia) &&
            getVideoUrl(selectedMedia) &&
            !isImageMedia(selectedMedia) && (
            <Tooltip title="Use last frame as reference to create a new video">
              <Button
                startIcon={<LastFrameIcon />}
                onClick={() => handleUseLastFrameAsReference(selectedMedia)}
                variant="outlined"
                size="small"
                sx={{
                  textTransform: "none",
                  fontFamily: "Lexend, sans-serif",
                  borderRadius: "8px",
                  borderColor: "#ddd",
                  color: "#555",
                  "&:hover": { borderColor: "#bbb", backgroundColor: "#fafafa" },
                }}
              >
                Last Frame as Reference
              </Button>
            </Tooltip>
          )}
          {/* Veo3: Extend button */}
          {selectedMedia &&
            isVeo3Media(selectedMedia) &&
            isMediaSuccess(selectedMedia) &&
            !selectedMedia.videoUrl1080p && (
              <Tooltip title="Once you fetch 1080p, the video cannot be extended">
                <Button
                  startIcon={<ExtendIcon />}
                  onClick={() => {
                    setExtendSourceMedia(selectedMedia as Veo3Media);
                    setExtendDialogOpen(true);
                  }}
                  variant="outlined"
                  size="small"
                  sx={{
                    textTransform: "none",
                    fontFamily: "Lexend, sans-serif",
                    borderRadius: "8px",
                    borderColor: "#ddd",
                    color: "#555",
                    "&:hover": { borderColor: "#bbb", backgroundColor: "#fafafa" },
                  }}
                >
                  Extend
                </Button>
              </Tooltip>
            )}
          {/* Veo3: 1080p button */}
          {selectedMedia &&
            isVeo3Media(selectedMedia) &&
            isMediaSuccess(selectedMedia) &&
            !selectedMedia.videoUrl1080p && (
              <Tooltip title="Fetch 1080p version (video cannot be extended after this)">
                <span>
                  <Button
                    startIcon={isFetching1080p ? <CircularProgress size={14} /> : <HdIcon />}
                    onClick={() => handleFetch1080p(selectedMedia as Veo3Media)}
                    disabled={isFetching1080p}
                    variant="outlined"
                    size="small"
                    sx={{
                      textTransform: "none",
                      fontFamily: "Lexend, sans-serif",
                      borderRadius: "8px",
                      borderColor: "#ddd",
                      color: "#555",
                      "&:hover": { borderColor: "#bbb", backgroundColor: "#fafafa" },
                      "&.Mui-disabled": { borderColor: "#eee", color: "#ccc" },
                    }}
                  >
                    {isFetching1080p ? "Loading..." : "1080p"}
                  </Button>
                </span>
              </Tooltip>
            )}
          {/* Veo3: 4K button */}
          {selectedMedia && isVeo3Media(selectedMedia) && isMediaSuccess(selectedMedia) && (
            <Button
              startIcon={
                selectedMedia.fourKState === "pending" ||
                selectedMedia.fourKState === "processing" ? (
                  <CircularProgress size={14} />
                ) : (
                  <FourKIcon />
                )
              }
              onClick={() => handleStart4K(selectedMedia as Veo3Media)}
              disabled={
                isStarting4K ||
                selectedMedia.fourKState === "pending" ||
                selectedMedia.fourKState === "processing" ||
                selectedMedia.fourKState === "success"
              }
              variant="outlined"
              size="small"
              sx={{
                textTransform: "none",
                fontFamily: "Lexend, sans-serif",
                borderRadius: "8px",
                borderColor: "#ddd",
                color: "#555",
                "&:hover": { borderColor: "#bbb", backgroundColor: "#fafafa" },
                "&.Mui-disabled": { borderColor: "#eee", color: "#ccc" },
              }}
            >
              {selectedMedia.fourKState === "success"
                ? "4K Ready"
                : selectedMedia.fourKState === "pending" ||
                    selectedMedia.fourKState === "processing"
                  ? "4K Processing..."
                  : "Upscale 4K"}
            </Button>
          )}
          {/* Veo3: 1080p indicator */}
          {selectedMedia && isVeo3Media(selectedMedia) && selectedMedia.videoUrl1080p && (
            <Typography
              variant="caption"
              sx={{
                color: "#4caf50",
                fontFamily: "Lexend, sans-serif",
                fontSize: "11px",
                alignSelf: "center",
              }}
            >
              Viewing 1080p
            </Typography>
          )}
          {selectedMedia && isMediaSuccess(selectedMedia) && !selectedMedia.assetUrl && (
            <Tooltip title="Save this video to your asset library so it remains accessible after the generation URL expires. You can also use it with templates or as a background.">
              <Button
                startIcon={isSavingToAssets ? <CircularProgress size={16} /> : <SaveAltIcon />}
                onClick={() => handleSaveToAssets(selectedMedia)}
                disabled={isSavingToAssets}
                variant="outlined"
                size="small"
                sx={{
                  textTransform: "none",
                  fontFamily: "Lexend, sans-serif",
                  borderRadius: "8px",
                  borderColor: "#ddd",
                  color: "#555",
                  "&:hover": { borderColor: "#bbb", backgroundColor: "#fafafa" },
                  "&.Mui-disabled": { borderColor: "#eee", color: "#ccc" },
                }}
              >
                {isSavingToAssets ? "Saving..." : "Save to Asset Library"}
              </Button>
            </Tooltip>
          )}
          {selectedMedia && selectedMedia.assetUrl && (
            <Button
              startIcon={<OpenInNewIcon />}
              onClick={() => window.open(selectedMedia.assetUrl!, "_blank")}
              variant="outlined"
              size="small"
              sx={{
                textTransform: "none",
                fontFamily: "Lexend, sans-serif",
                borderRadius: "8px",
                borderColor: "#ddd",
                color: "#555",
                "&:hover": { borderColor: "#bbb", backgroundColor: "#fafafa" },
              }}
            >
              Open Asset in New Tab
            </Button>
          )}
          {selectedMedia &&
            !isSeedanceMedia(selectedMedia) &&
            !isNanoBananaMedia(selectedMedia) &&
            !isGeminiMedia(selectedMedia) &&
            !isWan3Media(selectedMedia) &&
            !isHetznerMedia(selectedMedia) &&
            (selectedMedia.generationType === "text-to-video" ||
              selectedMedia.generationType === "image-to-video") &&
            showRemoveWatermarkButton(selectedMedia) && (
              <Tooltip
                title={isWorkspaceFree ? "Upgrade to a premium plan to remove watermarks" : ""}
              >
                <span>
                  <Button
                    startIcon={<WatermarkIcon />}
                    onClick={() => handleRemoveWatermark(selectedMedia)}
                    disabled={
                      isWorkspaceFree || selectedMedia?.watermarkRemovalStatus === "processing"
                    }
                    variant="outlined"
                    size="small"
                    sx={{
                      textTransform: "none",
                      fontFamily: "Lexend, sans-serif",
                      borderRadius: "8px",
                      borderColor: "#ddd",
                      color: "#555",
                      "&:hover": {
                        borderColor: "#bbb",
                        backgroundColor: "#fafafa",
                      },
                      "&.Mui-disabled": {
                        borderColor: "#eee",
                        color: "#ccc",
                      },
                    }}
                  >
                    {selectedMedia?.watermarkRemovalStatus === "processing"
                      ? "Removing..."
                      : "Remove Watermark"}
                  </Button>
                </span>
              </Tooltip>
            )}
          <Button
            startIcon={
              <DeleteIcon
                sx={{ color: selectedMedia?.state === "success" ? "#d32f2f" : undefined }}
              />
            }
            onClick={() => setPendingDeleteMedia(selectedMedia)}
            disabled={selectedMedia?.state !== "success"}
            variant="outlined"
            size="small"
            sx={{
              textTransform: "none",
              fontFamily: "Lexend, sans-serif",
              borderRadius: "8px",
              borderColor: selectedMedia?.state === "success" ? "#ffcdd2" : "#eee",
              color: selectedMedia?.state === "success" ? "#d32f2f" : "#ccc",
              "&:hover": {
                borderColor: "#ef9a9a",
                backgroundColor: "#fff5f5",
              },
              "&.Mui-disabled": {
                borderColor: "#eee",
                color: "#ccc",
              },
            }}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete confirmation dialog */}
      <Dialog
        open={!!pendingDeleteMedia}
        onClose={() => setPendingDeleteMedia(null)}
        maxWidth="xs"
        fullWidth
        PaperProps={{ sx: { borderRadius: "12px", fontFamily: "Lexend, sans-serif" } }}
      >
        <DialogTitle sx={{ fontFamily: "Lexend, sans-serif", fontWeight: 600, fontSize: "15px", color: "#111" }}>
          Delete video?
        </DialogTitle>
        <DialogContent>
          <Typography sx={{ color: "#555", fontFamily: "Lexend, sans-serif", fontSize: "14px" }}>
            This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
          <Button
            onClick={() => setPendingDeleteMedia(null)}
            variant="outlined"
            size="small"
            sx={{ textTransform: "none", fontFamily: "Lexend, sans-serif", borderRadius: "8px" }}
          >
            Cancel
          </Button>
          <Button
            onClick={() => { handleDelete(pendingDeleteMedia); setPendingDeleteMedia(null); }}
            variant="contained"
            size="small"
            sx={{
              textTransform: "none",
              fontFamily: "Lexend, sans-serif",
              borderRadius: "8px",
              backgroundColor: "#d32f2f",
              "&:hover": { backgroundColor: "#b71c1c" },
            }}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Veo3 Extend Dialog */}
      <Dialog
        open={extendDialogOpen}
        onClose={() => {
          setExtendDialogOpen(false);
          setExtendPrompt("");
          setExtendSourceMedia(null);
        }}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: "16px",
            fontFamily: "Lexend, sans-serif",
          },
        }}
      >
        <DialogContent sx={{ p: 3 }}>
          <Typography
            variant="h6"
            sx={{ fontFamily: "Lexend, sans-serif", fontWeight: 600, mb: 2 }}
          >
            Extend Video
          </Typography>
          <Typography
            variant="body2"
            sx={{ color: "#666", mb: 2, fontFamily: "Lexend, sans-serif", fontSize: "13px" }}
          >
            Create a continuation of the selected video with a new prompt.
          </Typography>
          <textarea
            style={{
              width: "100%",
              minHeight: "80px",
              padding: "12px",
              boxSizing: "border-box",
              border: "1px solid #e0e0e0",
              borderRadius: "12px",
              fontFamily: "Lexend, sans-serif",
              fontSize: "14px",
              resize: "none",
              outline: "none",
              marginBottom: "12px",
            }}
            placeholder="Describe what should happen next..."
            value={extendPrompt}
            onChange={(e) => setExtendPrompt(e.target.value)}
          />
          <Box sx={{ display: "flex", gap: 1.5, alignItems: "center" }}>
            <Typography
              variant="body2"
              sx={{ fontFamily: "Lexend, sans-serif", fontSize: "13px", color: "#555" }}
            >
              Model:
            </Typography>
            <Select
              size="small"
              value={extendModel}
              onChange={(e) => setExtendModel(e.target.value as Veo3ExtendModel)}
              sx={{
                fontFamily: "Lexend, sans-serif",
                fontSize: "12px",
                height: "30px",
                "& .MuiSelect-select": { py: 0.5, px: 1 },
              }}
            >
              <MenuItem value="fast">Fast</MenuItem>
              <MenuItem value="quality">Quality</MenuItem>
            </Select>
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={() => {
              setExtendDialogOpen(false);
              setExtendPrompt("");
              setExtendSourceMedia(null);
            }}
            sx={{ textTransform: "none", fontFamily: "Lexend, sans-serif" }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleExtend}
            disabled={!extendPrompt.trim() || isExtending}
            variant="contained"
            sx={{
              textTransform: "none",
              fontFamily: "Lexend, sans-serif",
              backgroundColor: THEME_COLORS.black,
              color: THEME_COLORS.darkYellow,
              "&:hover": { backgroundColor: "#333" },
            }}
          >
            {isExtending ? (
              <CircularProgress size={18} sx={{ color: THEME_COLORS.darkYellow }} />
            ) : (
              "Extend"
            )}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Prompt suggestion dialog (claude-worker, trained on winning ads) */}
      <Dialog
        open={promptSuggestionDialogOpen}
        onClose={() => {
          if (isGeneratingSuggestion) return;
          setPromptSuggestionDialogOpen(false);
          setPromptSuggestionInstructions("");
        }}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: "16px",
            fontFamily: "Lexend, sans-serif",
          },
        }}
      >
        <DialogContent sx={{ p: 3 }}>
          <Typography
            variant="h6"
            sx={{ fontFamily: "Lexend, sans-serif", fontWeight: 600, mb: 2 }}
          >
            Get Prompt Suggestion
          </Typography>
          <Typography
            variant="body2"
            sx={{ color: "#666", mb: 2, fontFamily: "Lexend, sans-serif", fontSize: "13px" }}
          >
            Describe what you're going for, and a Claude instance trained on winning ads will
            write an optimized prompt for you. It only fills the prompt field — nothing is
            generated until you hit submit yourself.
          </Typography>
          <textarea
            style={{
              width: "100%",
              minHeight: "100px",
              padding: "12px",
              boxSizing: "border-box",
              border: "1px solid #e0e0e0",
              borderRadius: "12px",
              fontFamily: "Lexend, sans-serif",
              fontSize: "14px",
              resize: "none",
              outline: "none",
            }}
            placeholder="e.g. a fitness supplement ad targeting young adults, energetic tone, beach setting..."
            value={promptSuggestionInstructions}
            onChange={(e) => setPromptSuggestionInstructions(e.target.value)}
            disabled={isGeneratingSuggestion}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={() => {
              setPromptSuggestionDialogOpen(false);
              setPromptSuggestionInstructions("");
            }}
            disabled={isGeneratingSuggestion}
            sx={{ textTransform: "none", fontFamily: "Lexend, sans-serif" }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleGetPromptSuggestion}
            disabled={!promptSuggestionInstructions.trim() || isGeneratingSuggestion}
            variant="contained"
            sx={{
              textTransform: "none",
              fontFamily: "Lexend, sans-serif",
              backgroundColor: THEME_COLORS.black,
              color: THEME_COLORS.darkYellow,
              "&:hover": { backgroundColor: "#333" },
            }}
          >
            {isGeneratingSuggestion ? (
              <CircularProgress size={18} sx={{ color: THEME_COLORS.darkYellow }} />
            ) : (
              "Generate"
            )}
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default PromptToVideo;
